import { fuzzyFilterAndScore } from "~/features/chat/utils/fuzzyFilter";

import { DndContext, DragOverlay } from "@dnd-kit/core";
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from "@hello-pangea/dnd";
import { useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import clsx from "clsx";
import {
  ArrowDownToDot,
  GripVertical,
  Merge,
  Minus,
  Sparkles,
  Trash2,
  X,
} from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import {
  FC,
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  getGetDoclistQueryKey,
  regenerateSectionTitlesMultiple,
  useCreateCategoryFromCategory,
  useCreateCategoryFromItem,
  useCreateCategoryFromSections,
  useDeleteDoclistCategory,
  useDeleteDoclistSection,
  useDeleteDoclistSections,
  useDeleteSectionPages,
  useExportAsyncChatDoclist,
  useExportAsyncChatDoclistDocx,
  useExtractSectionPages,
  useExtractSectionPagesToNewCategory,
  useGetChat,
  useMergeAllCategorySections,
  useMergeChatDoclistSections,
  useMergeSectionsMultiple,
  useRegenerateSectionTitle,
  useReorderChatDoclistSections,
  useReorderSectionsMultiple,
  useRotateSectionPages,
  useSortCategorySections,
  useSplitDoclistSection,
} from "~/api/chat/chat";
import { getListCustomReportsByCaseQueryKey } from "~/api/custom-reports/custom-reports";
import { DoclistTreeItem, DoclistTreeItemSectionsItem } from "~/api/model";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "~/components/ui/accordion";
import { Button } from "~/components/ui/button";
import { Checkbox } from "~/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui/tooltip";

import {
  useListOrganizationCategories,
  useListOrganizationCategoryFolders,
} from "~/api/organization/organization";
import {
  deserializeColor,
  getColorStyle,
} from "~/features/settings/components/categories/ColorPicker";
import { getVacantCategoryColor, useChatDocumentParams } from "~/helpers";
import { useToast } from "~/hooks/use-toast";
import { useDocumentSearchParams } from "~/hooks/useDocumentSearchParams";
import { trackEvent } from "~/utils/analytics";
import logger from "~/utils/logger";
import { hasAnyDuplicates } from "~/utils/textDuplicateDetection";
import { usePageAnnotations } from "../../../pageAnnotations/hooks/usePageAnnotations";
import { updateDocListSectionPage } from "../../../skyView/ui/docViewer/helpers/updateDocListSelectionPage";
import {
  DOCLIST_EXPORT_TYPE,
  EXPORT_WITH_DOCLIST_MAX_PAGES,
  MAX_MANUALLY_EXPANDED_SECTIONS,
} from "../../constants";
import { useDndForPages } from "../../hooks/useDndForPages";
import { useDndForSectionsAndCategories } from "../../hooks/useDndForSectionsAndCategories";
import { useDocList } from "../../hooks/useDocList";
import { useDoclistUpdatedAtStore } from "../../stores/doclistUpdatedAtStore";
import { usePageSelectionStore } from "../../stores/pageSelectionStore";
import { useSelectedEntityStore } from "../../stores/selectedEntityStore";
import { CategoryItem, DocListMode, PageInfo } from "../../types";
import { CancelExportDialog } from "../modals/CancelExportDialog";
import { DeleteCategoryDialog } from "../modals/DeleteCategoryDialog";
import { DeletePageDialog } from "../modals/DeletePageDialog";
import { DoclistOutdatedDialog } from "../modals/DoclistOutdatedDialog";
import { ExportDocListDialog } from "../modals/ExportDocListDialog";
import { ExtractPagesDialog } from "../modals/ExtractPagesDialog";
import { MergeAllCategoryDialog } from "../modals/MergeAllCategoryDialog";
import { MergeSectionDialog } from "../modals/MergeSectionDialog";
import { MoveToAlertDialog } from "../modals/MoveToAlertDialog";
import { PossibleDuplicatesModal } from "../modals/PossibleDuplicatesModal";
import { RotateSectionPagesDialog } from "../modals/RotateSectionPagesDialog";
import { SplitSectionDialog } from "../modals/SplitSectionDialog";
import { CategoryActions } from "./CategoryActions";
import DefineCategoryModal from "./DefineCategoryModal";
import { DocListActions } from "./DocListActions";
import { DocListCategoryInput } from "./DocListCategoryInput";
import { DocListCategorySection } from "./DocListCategorySection";
import { DocListPage } from "./DocListPage";
import { FloatingActionBar } from "./FloatingActionBar";
import { MergeSectionButton } from "./MergeSectionButton";
import { NewCategoryPlaceholder } from "./NewCategoryPlaceholder";

const TIMEOUT = 1500;
const LARGE_DOC_LIST_THRESHOLD = 600;
const INITIAL_EXPANDED_CATEGORY_COUNT = 5;
const SMALL_CATEGORY_SECTION_THRESHOLD = 20;

interface DocListProps {
  onSectionSelect?: () => void;
}

export const DocList: FC<DocListProps> = ({ onSectionSelect }) => {
  const { chatId, caseId, organizationId, isRestricted } =
    useChatDocumentParams();
  const {
    docList: doclist,
    isDocListLoading,
    absolutePageNumbers,
    firstPageIds,
  } = useDocList();

  const { data: chat } = useGetChat(organizationId, caseId, chatId);
  const { data: organizationCategories } =
    useListOrganizationCategories(organizationId);
  const { data: categoryFolders } =
    useListOrganizationCategoryFolders(organizationId);
  const { mutateAsync: exportDoclist, isPending: isExporting } =
    useExportAsyncChatDoclist();
  const { mutateAsync: exportDoclistDocx, isPending: isExportingDocx } =
    useExportAsyncChatDoclistDocx();
  const { mutateAsync: reorderSections } = useReorderChatDoclistSections();
  const { mutateAsync: mergeSections } = useMergeChatDoclistSections();
  const {
    mutateAsync: mergeAllCategorySections,
    isPending: isAllSectionsMerging,
  } = useMergeAllCategorySections();
  const { mutateAsync: mergeSectionsMultiple } = useMergeSectionsMultiple();
  const { mutateAsync: splitSection } = useSplitDoclistSection();
  const { mutateAsync: createCategory } = useCreateCategoryFromItem();
  const { mutateAsync: createCategoryMultiple } =
    useCreateCategoryFromSections();
  const { mutateAsync: createCategoryFromCategory } =
    useCreateCategoryFromCategory();
  const { mutateAsync: deleteCategory } = useDeleteDoclistCategory();
  const { mutateAsync: deleteSection } = useDeleteDoclistSection();
  const { mutateAsync: deleteSections } = useDeleteDoclistSections();
  const { mutateAsync: sortSections } = useSortCategorySections();
  const { mutateAsync: moveSections } = useReorderSectionsMultiple();
  const { mutateAsync: regenerateSectionTitle } = useRegenerateSectionTitle();
  const { mutateAsync: rotateSectionPages } = useRotateSectionPages();
  const { mutateAsync: deletePage } = useDeleteSectionPages();
  const { mutateAsync: extractPages } = useExtractSectionPages();
  const { mutateAsync: extractPagesToNewCategory } =
    useExtractSectionPagesToNewCategory();
  const { annotations: allAnnotations } = usePageAnnotations(
    organizationId,
    caseId
  );
  const pageAnnotationColors = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const a of allAnnotations) {
      const existing = map.get(a.pageId);
      if (existing) {
        existing.push(a.color);
      } else {
        map.set(a.pageId, [a.color]);
      }
    }
    return map;
  }, [allAnnotations]);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const intl = useIntl();
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [mode, setMode] = useState<DocListMode>(DocListMode.VIEW);
  const isExportMode = mode === DocListMode.EXPORT;
  const { setUpdatedAt } = useDoclistUpdatedAtStore();
  const updatedAt = useDoclistUpdatedAtStore((state) =>
    caseId ? state.updatedAtByProject[caseId] : undefined
  );
  const [exportSectionIds, setExportSectionIds] = useState<string[]>([]);
  const [items, setItems] = useState<DoclistTreeItem[]>([]);
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCount, setSelectedCount] = useState(0);
  const [isCategoryRenaming, setIsCategoryRenaming] = useState(false);
  const [renamedCategoryId, setRenamedCategoryId] = useState<number | null>(
    null
  );
  const [editedCategoryId, setEditedCategoryId] = useState<number | null>(null);
  const [isSuccessfullyExported, setIsSuccessfullyExported] = useState(false);
  const [nameOfExportedFile, setNameOfExportedFile] = useState("");
  const [showCancelExportDialog, setShowCancelExportDialog] = useState(false);
  const [showDeleteCategoryDialog, setShowDeleteCategoryDialog] =
    useState(false);
  const [deletedItem, setDeletedItem] = useState<DoclistTreeItem | null>(null);
  const [hoveredCategoryId, setHoveredCategoryId] = useState<number | null>(
    null
  );
  const [sortByNameOrder, setSortByNameOrder] = useState<
    Record<number, "asc" | "desc" | null>
  >({});
  const [sortByDateOrder, setSortByDateOrder] = useState<
    Record<number, "asc" | "desc" | null>
  >({});
  const [sortByPageCountOrder, setSortByPageCountOrder] = useState<
    Record<number, "asc" | "desc" | null>
  >({});
  const [isDoclistOutdated, setIsDoclistOutdated] = useState(false);
  const [{ sectionId, pageId, tab }, updateDocumentParams] =
    useDocumentSearchParams();
  const { setSelectedEntity: setSelectedEntityInStore } =
    useSelectedEntityStore();
  const {
    selectedPageIds: multiSelectedPageIds,
    handlePageClick: handleMultiSelectPageClick,
    clearSelection: clearMultiSelection,
    isMultiSelectActive,
  } = usePageSelectionStore();
  const isDoclistLoaded = useRef(false);
  const [categoryPlaceholders, setCategoryPlaceholders] = useState<
    CategoryItem[]
  >([]);
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [newCategoryColor, setNewCategoryColor] = useState<string | null>(null);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [hoveredMergeLine, setHoveredMergeLine] = useState<{
    categoryId: number;
    index: number;
  } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [showMergeSectionDialog, setShowMergeSectionDialog] = useState(false);
  const [mergeDialogData, setMergeDialogData] = useState<{
    sourceSection: DoclistTreeItemSectionsItem;
    targetSection: DoclistTreeItemSectionsItem | null;
  } | null>(null);

  const [mergeAllDialogData, setMergeAllDialogData] = useState<{
    category: DoclistTreeItem;
  } | null>(null);
  const [showPossibleDuplicatesModal, setShowPossibleDuplicatesModal] =
    useState(false);

  const [selectedSection, setSelectedSection] =
    useState<DoclistTreeItemSectionsItem | null>(null);
  const [showDeleteSectionDialog, setShowDeleteSectionDialog] = useState(false);
  const [showSplitSectionDialog, setShowSplitSectionDialog] = useState(false);
  const [showMoveToAlertDialog, setShowMoveToAlertDialog] = useState(false);
  const [showExtractPagesDialog, setShowExtractPagesDialog] = useState(false);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [showRotateSectionDialog, setShowRotateSectionDialog] = useState(false);
  const [isRotatingSectionPages, setIsRotatingSectionPages] = useState(false);

  const [selectedSectionsForMerge, setSelectedSectionsForMerge] = useState<
    Set<string>
  >(new Set());
  const [lastSelectedSectionId, setLastSelectedSectionId] = useState<
    string | null
  >(null);
  const [isMergingSelectedSections, setIsMergingSelectedSections] =
    useState(false);
  const [aiRenamingSectionId, setAiRenamingSectionId] = useState<string | null>(
    null
  );
  const [isAiRenamingMultiple, setIsAiRenamingMultiple] = useState(false);
  const [isWideContainer, setIsWideContainer] = useState(false);
  const [isRotatingPages, setIsRotatingPages] = useState<boolean>(false);
  const [isDeletingPages, setIsDeletingPages] = useState<boolean>(false);
  const [isExtractingPages, setIsExtractingPages] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const computeDefaultExpandedKeys = useCallback((tree: DoclistTreeItem[]) => {
    const totalSectionCount = tree.reduce(
      (acc, item) => acc + item.sections.length,
      0
    );

    if (totalSectionCount > LARGE_DOC_LIST_THRESHOLD) {
      const smallCategoryKeys = tree
        .filter(
          (item) => item.sections.length <= SMALL_CATEGORY_SECTION_THRESHOLD
        )
        .map((item) => `item-${item.category.id}`);

      if (smallCategoryKeys.length > 0) {
        return smallCategoryKeys;
      }

      const fallbackKeys = [...tree]
        .sort((a, b) => a.sections.length - b.sections.length)
        .slice(0, INITIAL_EXPANDED_CATEGORY_COUNT)
        .map((item) => `item-${item.category.id}`);

      return fallbackKeys;
    }

    return tree.map((item) => `item-${item.category.id}`);
  }, []);

  useEffect(() => {
    if (doclist && !isDoclistLoaded.current) {
      trackEvent({ eventName: "app/doc_list/visit" });
      isDoclistLoaded.current = true;
    }
  }, [doclist]);

  useEffect(() => {
    if (doclist) {
      setItems(doclist.tree);
      if (isFirstLoad) {
        setExpandedItems(computeDefaultExpandedKeys(doclist.tree));
        setIsFirstLoad(false);
      }
      setUpdatedAt(caseId, doclist.updatedAt);
    }
  }, [doclist, caseId, isFirstLoad, computeDefaultExpandedKeys, setUpdatedAt]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setIsWideContainer(entry.contentRect.width >= 500);
      }
    });

    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    if (isRestricted) {
      clearMultiSelection();
    }
  }, [isRestricted]);

  const handleCategoryCheckboxChange = useCallback(
    (categoryId: number, checked: boolean) => {
      const sectionIds =
        items
          ?.find((item) => item.category.id === categoryId)
          ?.sections.map((section) => section.id) || [];

      setExportSectionIds((prev) => {
        const newIds = checked
          ? [...new Set([...prev, ...sectionIds])]
          : prev.filter((id) => !sectionIds.includes(id));

        setSelectedCount(newIds.length);
        return newIds;
      });
    },
    [items]
  );

  const handleSectionCheckboxChange = useCallback(
    (sectionId: string, checked: boolean) => {
      trackEvent({
        eventName: "app/doc_list/select_document.checkbox/click",
        properties: {
          sectionId,
          checked,
        },
      });
      setExportSectionIds((prev) => {
        const newIds = checked
          ? [...prev, sectionId]
          : prev.filter((id) => id !== sectionId);

        setSelectedCount(newIds.length);
        return newIds;
      });
    },
    []
  );

  const filteredSections = useMemo(() => {
    if (!searchTerm) return items;

    return items.map((item) => {
      const scored = fuzzyFilterAndScore(
        item.sections,
        searchTerm,
        (s) => s.name
      );
      return {
        ...item,
        sections: scored.map((s) => s.item),
      };
    });
  }, [items, searchTerm]);

  const deferredFilteredSections = useDeferredValue(filteredSections);
  const prevSearchTermRef = useRef(searchTerm);

  useEffect(() => {
    if (!searchTerm) {
      return;
    }

    setExpandedItems((prev) => {
      const matchingKeys = filteredSections
        .filter((item) => item.sections.length > 0)
        .map((item) => `item-${item.category.id}`);

      let didChange = false;
      const next = new Set(prev);

      matchingKeys.forEach((key) => {
        if (!next.has(key)) {
          next.add(key);
          didChange = true;
        }
      });

      if (!didChange) {
        return prev;
      }

      return Array.from(next);
    });
  }, [filteredSections, searchTerm]);

  useEffect(() => {
    if (!searchTerm && prevSearchTermRef.current) {
      setExpandedItems(computeDefaultExpandedKeys(items));
    }

    prevSearchTermRef.current = searchTerm;
  }, [searchTerm, items, computeDefaultExpandedKeys]);

  const activeCategoryId = useMemo(() => {
    return items.find((item) =>
      item.sections.some((section) => section.id === sectionId)
    )?.category.id;
  }, [items, sectionId]);

  const selectedSectionIdsSet = useMemo(
    () => new Set(exportSectionIds),
    [exportSectionIds]
  );

  const selectedPageCount = useMemo(() => {
    if (exportSectionIds.length === 0) return 0;
    return items.reduce((total, item) => {
      return (
        total +
        item.sections
          .filter((s) => selectedSectionIdsSet.has(s.id))
          .reduce((sum, s) => sum + s.pageCount, 0)
      );
    }, 0);
  }, [exportSectionIds, items, selectedSectionIdsSet]);

  const selectedCategoryIds = useMemo(() => {
    const result = new Set<number>();
    if (selectedSectionIdsSet.size === 0) {
      return result;
    }

    items.forEach((item) => {
      if (
        item.sections.some((section) => selectedSectionIdsSet.has(section.id))
      ) {
        result.add(item.category.id);
      }
    });

    return result;
  }, [items, selectedSectionIdsSet]);

  // Map category ID to its folder's color (if it belongs to a folder)
  const categoryIdToFolderColor = useMemo(() => {
    const map = new Map<number, string>();
    categoryFolders?.forEach((folder) => {
      // Each category in the folder should use the folder's color
      folder.categories.forEach((cat) => {
        map.set(cat.id, folder.color);
      });
    });
    return map;
  }, [categoryFolders]);

  const organizationCategoriesById = useMemo(() => {
    const map = new Map<number, { color: string }>();
    organizationCategories?.forEach((cat) => {
      map.set(cat.id, { color: cat.color });
    });
    return map;
  }, [organizationCategories]);

  const categoryBackgrounds = useMemo(() => {
    const backgrounds = new Map<number, React.CSSProperties>();

    items.forEach((item) => {
      const categoryId = item.category.id;
      const organizationCategoryId = item.categoryId;
      // Priority: folder color > organization category color > doclist category color
      const folderColor = categoryIdToFolderColor.get(organizationCategoryId);
      const orgCategory = organizationCategoriesById.get(
        organizationCategoryId
      );
      const colorString =
        folderColor ?? orgCategory?.color ?? item.category.color;
      const colorValue = deserializeColor(colorString);
      const baseStyle = getColorStyle(colorValue);
      const isHovered = hoveredCategoryId === categoryId;

      const isActive = isExportMode
        ? selectedCategoryIds.has(categoryId)
        : [activeCategoryId, editedCategoryId].includes(categoryId) ||
          !!searchTerm ||
          isHovered;

      if (isActive) {
        // For active state, layer gradient overlay on top of pattern
        const gradientOverlay =
          "linear-gradient(90deg, rgba(255, 255, 255, 0) 5.24%, rgba(255, 255, 255, 0.25) 93.21%)";
        const patternImage = baseStyle.backgroundImage;

        backgrounds.set(categoryId, {
          ...baseStyle,
          backgroundImage: patternImage
            ? `${gradientOverlay}, ${patternImage}`
            : gradientOverlay,
        });
      } else {
        // For inactive state, apply reduced opacity to the color
        backgrounds.set(categoryId, {
          ...baseStyle,
          backgroundColor: `${colorValue.hex}40`,
        });
      }
    });

    return backgrounds;
  }, [
    items,
    hoveredCategoryId,
    isExportMode,
    selectedCategoryIds,
    activeCategoryId,
    editedCategoryId,
    searchTerm,
    categoryIdToFolderColor,
    organizationCategoriesById,
  ]);

  const getCategoryTextColor = useCallback(
    (categoryId: number) => {
      if (isExportMode) {
        return selectedCategoryIds.has(categoryId)
          ? "text-white"
          : "text-foreground dark:text-white";
      }
      const isActive =
        [activeCategoryId, editedCategoryId].includes(categoryId) ||
        !!searchTerm ||
        hoveredCategoryId === categoryId;
      return isActive ? "text-white" : "text-foreground";
    },
    [
      isExportMode,
      selectedCategoryIds,
      activeCategoryId,
      editedCategoryId,
      searchTerm,
      hoveredCategoryId,
    ]
  );

  const formattedDate = new Date().toLocaleString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "numeric",
  });

  //@hello-pangea/dnd for sections and categories
  const { handleDragEnd: originalHandleDragEnd } =
    useDndForSectionsAndCategories(
      items,
      setItems,
      filteredSections,
      organizationId,
      caseId,
      isRestricted,
      () => {
        if (!isMergingSelectedSections) {
          setIsDoclistOutdated(true);
        }
      },
      categoryPlaceholders,
      setCategoryPlaceholders
    );

  const handleDragEnd = (result: DropResult) => {
    setIsDragging(false);
    originalHandleDragEnd(result);
  };

  const handleExport = async (type: DOCLIST_EXPORT_TYPE) => {
    trackEvent({
      eventName: "app/doc_list/export_list.button/click",
    });

    if (
      type === DOCLIST_EXPORT_TYPE.DOCX ||
      type === DOCLIST_EXPORT_TYPE.TOC_DOCX
    ) {
      const tocOnly = type === DOCLIST_EXPORT_TYPE.TOC_DOCX;
      const tocOnlyText = tocOnly ? "TOC - " : "";
      setNameOfExportedFile(
        `${tocOnlyText}Doclist: ${chat?.title} - ${formattedDate}.docx`
      );
      await exportDoclistDocx({
        organizationId,
        projectId: caseId,
        chatId,
        data: {
          sectionIds: exportSectionIds,
          date: formattedDate,
          tocOnly,
        },
      });
      setIsExportDialogOpen(true);
      setMode(DocListMode.VIEW);
      setIsSuccessfullyExported(true);
      return;
    }

    const tocOnly = type === DOCLIST_EXPORT_TYPE.TOC_PDF;

    const exportData = {
      sectionIds: exportSectionIds,
      date: formattedDate,
      tocOnly,
    };

    const tocOnlyText = tocOnly ? "TOC - " : "";

    setNameOfExportedFile(
      `${tocOnlyText}Doclist: ${chat?.title} - ${formattedDate}.pdf`
    );

    await exportDoclist({
      organizationId,
      projectId: caseId,
      chatId,
      data: exportData,
    });
    setIsExportDialogOpen(true);
    setMode(DocListMode.VIEW);
    setIsSuccessfullyExported(true);
  };

  useEffect(() => {
    if (isSuccessfullyExported) {
      const timer = setTimeout(() => {
        setIsSuccessfullyExported(false);
      }, TIMEOUT);

      return () => clearTimeout(timer);
    }
  }, [isSuccessfullyExported]);

  const handleKeepExporting = () => {
    setShowCancelExportDialog(false);
  };

  const handleDiscardExporting = () => {
    setShowCancelExportDialog(false);
    setExportSectionIds([]);
    setSelectedCount(0);
    setMode(DocListMode.VIEW);
  };

  const handleCancelExportClick = () => {
    trackEvent({
      eventName: "app/doc_list/cancel.button/click",
    });
    if (exportSectionIds.length) {
      setShowCancelExportDialog(true);
    } else {
      handleDiscardExporting();
    }
  };

  const handleSectionClick = useCallback(
    (section: DoclistTreeItemSectionsItem, event?: React.MouseEvent) => {
      if (!section.pageCount) return;

      if (event && (event.shiftKey || event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        event.stopPropagation();

        const allSections = items.flatMap((item) => item.sections);

        if (event.shiftKey) {
          if (lastSelectedSectionId) {
            const lastIndex = allSections.findIndex(
              (s) => s.id === lastSelectedSectionId
            );
            const currentIndex = allSections.findIndex(
              (s) => s.id === section.id
            );

            if (lastIndex !== -1 && currentIndex !== -1) {
              const start = Math.min(lastIndex, currentIndex);
              const end = Math.max(lastIndex, currentIndex);
              const newSelected = new Set(selectedSectionsForMerge);

              for (let i = start; i <= end; i++) {
                const sectionInRange = allSections[i];
                if (sectionInRange) {
                  newSelected.add(sectionInRange.id);
                }
              }

              setSelectedSectionsForMerge(newSelected);
              setLastSelectedSectionId(section.id);
              return;
            }
          } else {
            const newSelected = new Set(selectedSectionsForMerge);
            newSelected.add(section.id);
            setSelectedSectionsForMerge(newSelected);
            setLastSelectedSectionId(section.id);
            return;
          }
        }

        if (event.metaKey || event.ctrlKey) {
          const newSelected = new Set(selectedSectionsForMerge);

          if (
            newSelected.size === 0 &&
            lastSelectedSectionId &&
            lastSelectedSectionId !== section.id
          ) {
            newSelected.add(lastSelectedSectionId);
          }

          if (newSelected.has(section.id)) {
            newSelected.delete(section.id);
          } else {
            newSelected.add(section.id);
          }
          setSelectedSectionsForMerge(newSelected);
          setLastSelectedSectionId(section.id);
          return;
        }
      }

      setLastSelectedSectionId(section.id);

      const willParamsChange =
        sectionId !== section.id || pageId !== firstPageIds[section.id];
      if (willParamsChange) {
        skipSectionAutoExpandRef.current = true;
      }

      if (tab === "summary") {
        setSelectedEntityInStore("section", section.id, section.pageIds || []);
      }

      clearMultiSelection();
      setSelectedPageId(null);

      updateDocumentParams({
        sectionId: section.id,
        pageId: firstPageIds[section.id],
      });

      onSectionSelect?.();
    },
    [
      firstPageIds,
      updateDocumentParams,
      items,
      selectedSectionsForMerge,
      lastSelectedSectionId,
      tab,
      setSelectedEntityInStore,
      onSectionSelect,
      sectionId,
      pageId,
    ]
  );

  const handleSelectedCount = useCallback(
    (checked: boolean) => {
      if (checked) {
        trackEvent({ eventName: "app/doc_list/select_all.checkbox/click" });
        const allSectionIds = items.flatMap((item) =>
          item.sections.map((section) => section.id)
        );
        setExportSectionIds(allSectionIds);
        setSelectedCount(allSectionIds.length);
      } else {
        setExportSectionIds([]);
        setSelectedCount(0);
      }
    },
    [items]
  );

  const handleRenameClick = (item: DoclistTreeItem) => {
    trackEvent({
      eventName:
        "app/doc_list/category_actions_menu/category_rename.link/click",
    });
    const categoryId = item.category.id;
    setRenamedCategoryId(() => categoryId);
    setIsCategoryRenaming(true);
  };

  const handleSectionSplit = async (
    sections: [DoclistTreeItemSectionsItem, DoclistTreeItemSectionsItem]
  ) => {
    if (!updatedAt) return;

    const placeholderSectionId = sections[1].id;
    const previousItems = items;

    setItems((prevItems) =>
      prevItems.map((item) => {
        if (item.categoryId === sections[0].categoryId) {
          const originalSectionIndex = item.sections.findIndex(
            (s) => s.id === sections[0].id
          );
          if (originalSectionIndex === -1) return item;

          const newSections = [...item.sections];
          newSections.splice(originalSectionIndex, 1);
          newSections.splice(originalSectionIndex, 0, sections[0], sections[1]);

          return {
            ...item,
            sections: newSections,
          };
        }
        return item;
      })
    );
    try {
      const response = await splitSection({
        organizationId,
        projectId: caseId,
        chatId,
        data: {
          source: {
            sectionId: sections[0].id,
            title: sections[0].name,
          },
          target: {
            startPageId: sections[1].pageIds[0],
            title: sections[1].name,
          },
          updatedAt: updatedAt,
        },
      });
      setUpdatedAt(caseId, response.updatedAt);
      setItems((currentItems) =>
        currentItems.map((item) => {
          if (item.categoryId !== response.section.categoryId) {
            return item;
          }

          const sectionIndex = item.sections.findIndex(
            (section) => section.id === placeholderSectionId
          );

          if (sectionIndex === -1) {
            return item;
          }

          const updatedSections = [...item.sections];
          updatedSections[sectionIndex] = {
            ...updatedSections[sectionIndex],
            ...response.section,
          };

          return {
            ...item,
            sections: updatedSections,
          };
        })
      );
      queryClient.invalidateQueries({
        queryKey: getGetDoclistQueryKey(organizationId, caseId),
      });
      queryClient.invalidateQueries({
        queryKey: getListCustomReportsByCaseQueryKey(organizationId, caseId),
      });
    } catch (error) {
      setItems(previousItems);
      if (error instanceof AxiosError) {
        if (error.response?.status === 409) {
          setIsDoclistOutdated(true);
        }
      }
      logger.error("Failed to merge sections:", error);
    } finally {
      setShowSplitSectionDialog(false);
      setSelectedSection(null);
    }
  };

  const handleMergeLineClick = async (categoryId: number, index: number) => {
    const sourceCategory = items.find(
      (item) => item.category.id === categoryId
    );
    if (!sourceCategory) return;

    const sourceSection = sourceCategory.sections[index - 1];
    const targetSection = sourceCategory.sections[index];

    if (!sourceSection || !targetSection) return;

    setMergeDialogData({
      sourceSection,
      targetSection,
    });
    setShowMergeSectionDialog(true);
  };

  const handleSectionMerge = useCallback(
    async (
      mergedSection: DoclistTreeItemSectionsItem,
      removedSectionId: string
    ): Promise<number | undefined> => {
      if (!updatedAt) return undefined;

      const previousItems = items;
      const newItems = items.map((item) => ({
        ...item,
        sections: [...item.sections],
      }));

      const targetCategory = newItems.find((item) =>
        item.sections.some((s) => s.id === mergedSection.id)
      );
      const targetSectionIndex = targetCategory?.sections.findIndex(
        (s) => s.id === mergedSection.id
      );

      const sourceCategory = newItems.find((item) =>
        item.sections.some((s) => s.id === removedSectionId)
      );
      const sourceSection = sourceCategory?.sections.find(
        (s) => s.id === removedSectionId
      );

      if (
        !targetCategory ||
        targetSectionIndex === undefined ||
        targetSectionIndex === -1 ||
        !sourceCategory ||
        !sourceSection
      ) {
        return undefined;
      }

      const targetSection = { ...targetCategory.sections[targetSectionIndex] };
      targetSection.pageIds = [
        ...targetSection.pageIds,
        ...sourceSection.pageIds,
      ];
      targetSection.pages = [
        ...(targetSection.pages ?? []),
        ...(sourceSection.pages ?? []),
      ];
      targetSection.pageCount =
        targetSection.pageCount + sourceSection.pageCount;

      targetCategory.sections[targetSectionIndex] = targetSection;

      const sourceSectionIndex = sourceCategory.sections.findIndex(
        (s) => s.id === removedSectionId
      );
      sourceCategory.sections.splice(sourceSectionIndex, 1);

      if (sourceCategory.sections.length === 0) {
        const categoryIndex = newItems.findIndex(
          (item) => item.category.id === sourceCategory.category.id
        );
        if (categoryIndex !== -1) {
          newItems.splice(categoryIndex, 1);
        }
      }

      setItems([...newItems]);
      try {
        const response = await mergeSections({
          organizationId,
          projectId: caseId,
          chatId,
          data: {
            source: {
              id: removedSectionId,
            },
            target: {
              id: mergedSection.id,
              title: mergedSection.name,
            },
            updatedAt: updatedAt,
          },
        });
        setUpdatedAt(caseId, response.updatedAt);
        queryClient.invalidateQueries({
          queryKey: getGetDoclistQueryKey(organizationId, caseId),
        });
        queryClient.invalidateQueries({
          queryKey: getListCustomReportsByCaseQueryKey(organizationId, caseId),
        });
        return response.updatedAt;
      } catch (error) {
        setItems(previousItems);
        if (error instanceof AxiosError) {
          if (error.response?.status === 409) {
            setIsDoclistOutdated(true);
          }
        }
        logger.error("Failed to merge sections:", error);
        throw error;
      }
    },
    [
      items,
      updatedAt,
      mergeSections,
      organizationId,
      caseId,
      chatId,
      queryClient,
    ]
  );

  const handleSectionDelete = async (section: DoclistTreeItemSectionsItem) => {
    if (!updatedAt) return;
    const newItems = items
      .map((item) => {
        if (item.sections.some((s) => s.id === section.id)) {
          const updatedSections = item.sections.filter(
            (s) => s.id !== section.id
          );

          if (updatedSections.length === 0) {
            return null;
          }

          return {
            ...item,
            sections: updatedSections,
          };
        }
        return item;
      })
      .filter((item): item is DoclistTreeItem => item !== null);

    setItems(newItems);
    try {
      const response = await deleteSection({
        organizationId,
        projectId: caseId,
        chatId,
        sectionId: section.id,
        data: {
          updatedAt: updatedAt,
        },
      });
      setUpdatedAt(caseId, response.updatedAt);
      queryClient.invalidateQueries({
        queryKey: getGetDoclistQueryKey(organizationId, caseId),
      });
      queryClient.invalidateQueries({
        queryKey: getListCustomReportsByCaseQueryKey(organizationId, caseId),
      });
      toast({
        title: intl.formatMessage({
          id: "docList.deleteSection.success",
          defaultMessage: "Section deleted",
        }),
        description: intl.formatMessage({
          id: "docList.deleteSection.successDescription",
          defaultMessage: "The section has been successfully removed.",
        }),
      });
    } catch (error) {
      setItems([...items]);
      if (error instanceof AxiosError) {
        if (error.response?.status === 409) {
          setIsDoclistOutdated(true);
          return;
        }
      }
      toast({
        variant: "destructive",
        title: intl.formatMessage({
          id: "error",
          defaultMessage: "Error",
        }),
        description: intl.formatMessage({
          id: "docList.deleteSection.error",
          defaultMessage: "Failed to delete the section. Please try again.",
        }),
      });
      logger.error("Failed to delete section:", error);
    }
  };

  const handleMoveSection = async (
    section: DoclistTreeItemSectionsItem | null,
    category: CategoryItem
  ) => {
    if (!updatedAt || !section) return;

    if (category.isUnsaved) {
      try {
        const response = await createCategory({
          organizationId,
          projectId: caseId,
          data: {
            source: {
              type: "section",
              id: section.id,
              title: category.name,
              color: category.color,
            },
            updatedAt,
          },
        });
        setUpdatedAt(caseId, response.updatedAt);
        queryClient.invalidateQueries({
          queryKey: getGetDoclistQueryKey(organizationId, caseId),
        });
        queryClient.invalidateQueries({
          queryKey: getListCustomReportsByCaseQueryKey(organizationId, caseId),
        });
        setCategoryPlaceholders((prev) =>
          prev.filter((p) => p.id !== category.id)
        );
      } catch (error) {
        setItems([...items]);
        if (error instanceof AxiosError) {
          if (error.response?.status === 409) {
            setIsDoclistOutdated(true);
          }
        }
        logger.error("Failed to create category:", error);
      }
      return;
    }

    const destCategoryIndex = items.findIndex(
      (item) => item.category.id === category.id
    );

    if (destCategoryIndex === -1) return;
    const destCategory = items[destCategoryIndex];
    const prevId = destCategory.sections[destCategory.sections.length - 1].id;

    try {
      const response = await reorderSections({
        organizationId,
        projectId: caseId,
        data: {
          sectionId: section.id,
          target: {
            prevId,
            folderCategoryId: category.id,
          },
          updatedAt: updatedAt,
        },
      });
      setUpdatedAt(caseId, response.updatedAt);
      queryClient.invalidateQueries({
        queryKey: getGetDoclistQueryKey(organizationId, caseId),
      });
      queryClient.invalidateQueries({
        queryKey: getListCustomReportsByCaseQueryKey(organizationId, caseId),
      });
      toast({
        title: intl.formatMessage({
          id: "docList.moveSection.success",
          defaultMessage: "Section moved",
        }),
        description: intl.formatMessage({
          id: "docList.moveSection.successDescription",
          defaultMessage: "The sections have been successfully moved.",
        }),
      });
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 409) {
          setIsDoclistOutdated(true);
          return;
        }
      }
      toast({
        variant: "destructive",
        title: intl.formatMessage({
          id: "error",
          defaultMessage: "Error",
        }),
        description: intl.formatMessage({
          id: "docList.moveSection.error",
          defaultMessage: "Failed to move the section. Please try again.",
        }),
      });
      logger.error("Failed to move section:", error);
    }
  };

  const handleAddCategory = () => {
    trackEvent({
      eventName: "app/doc_list/add_category.button/click",
    });
    const allCategoryColors = items?.map((item) => item.category.color) || [];
    const allPlaceholderColors = categoryPlaceholders.map(
      (placeholder) => placeholder.color
    );
    const allColors = [...allCategoryColors, ...allPlaceholderColors];
    const color = getVacantCategoryColor(allColors);
    setNewCategoryColor(color);
    setIsAddingCategory(true);
  };

  const handleCategoryCreated = (name: string) => {
    if (!newCategoryColor) return;
    setCategoryPlaceholders((prev) => [
      { id: Date.now(), color: newCategoryColor, name, isUnsaved: true },
      ...prev,
    ]);
    setIsAddingCategory(false);
    setNewCategoryColor(null);
  };

  const handleCategoryPlaceholderRename = (id: number, newName: string) => {
    setCategoryPlaceholders((prev) =>
      prev.map((placeholder) =>
        placeholder.id === id ? { ...placeholder, name: newName } : placeholder
      )
    );
  };

  const handleCategoryPlaceholderDelete = (id: number) => {
    setCategoryPlaceholders((prev) =>
      prev.filter((placeholder) => placeholder.id !== id)
    );
  };

  const handleCategoryCancel = () => {
    setIsAddingCategory(false);
    setNewCategoryColor(null);
  };

  const handleDeleteCategory = (item: DoclistTreeItem) => {
    trackEvent({
      eventName:
        "app/doc_list/category_actions_menu/category_delete.link/click",
    });
    setDeletedItem(item);
    setShowDeleteCategoryDialog(true);
  };

  const [showDefineCategoryModal, setShowDefineCategoryModal] = useState(false);
  const [defineCategoryTarget, setDefineCategoryTarget] =
    useState<DoclistTreeItem | null>(null);
  const [defineCategoryMode, setDefineCategoryMode] = useState<
    "new" | "redefine"
  >("new");

  /**
   * Returns whether the given doclist item is linked to an org-level
   * DocumentCategory with a non-empty definition. Used to gate the
   * "Add Category" vs "Refine Category" menu items.
   */
  const hasOrgDefinition = useCallback(
    (item: DoclistTreeItem): boolean => {
      const docCategoryId = item.category.documentCategoryId;
      if (!docCategoryId) return false;
      const orgCategory = organizationCategories?.find(
        (cat) => cat.id === docCategoryId
      );
      return Boolean(orgCategory?.definition);
    },
    [organizationCategories]
  );

  const handleDefineCategory = (item: DoclistTreeItem) => {
    setDefineCategoryTarget(item);
    setDefineCategoryMode("new");
    setShowDefineCategoryModal(true);
  };

  const handleRedefineCategory = (item: DoclistTreeItem) => {
    setDefineCategoryTarget(item);
    setDefineCategoryMode("redefine");
    setShowDefineCategoryModal(true);
  };

  const handleDeleteCategoryCancel = () => {
    trackEvent({
      eventName: "app/doc_list/delete_category_modal/cancel.button/click",
    });
    setDeletedItem(null);
    setShowDeleteCategoryDialog(false);
  };

  const handleDeleteCategoryConfirm = async (category: CategoryItem) => {
    trackEvent({
      eventName: "app/doc_list/delete_category_modal/confirm.button/click",
    });
    setShowDeleteCategoryDialog(false);
    if (!deletedItem || !updatedAt) return;

    const newItems = items.map((item) => ({
      ...item,
      sections: [...item.sections],
    }));
    const deletedCategory = newItems.find(
      (item) => item.category.id === deletedItem.category.id
    );
    if (!deletedCategory) return;

    if (category.isUnsaved) {
      setItems([
        ...newItems.filter(
          (item) => item.category.id !== deletedItem.category.id
        ),
      ]);

      try {
        const response = await createCategoryFromCategory({
          organizationId,
          projectId: caseId,
          data: {
            source: {
              categoryId: deletedCategory.categoryId,
              title: category.name,
              color: category.color,
            },
            updatedAt,
          },
        });
        setUpdatedAt(caseId, response.updatedAt);
        setItems((currentItems) => [response.node, ...currentItems]);

        queryClient.invalidateQueries({
          queryKey: getGetDoclistQueryKey(organizationId, caseId),
        });
        queryClient.invalidateQueries({
          queryKey: getListCustomReportsByCaseQueryKey(organizationId, caseId),
        });
        setCategoryPlaceholders((prev) =>
          prev.filter((p) => p.id !== category.id)
        );
      } catch (error) {
        setItems([...items]);
        if (error instanceof AxiosError) {
          if (error.response?.status === 409) {
            setIsDoclistOutdated(true);
          }
        }
        logger.error("Failed to create category:", error);
      }
      return;
    }

    if (category.isUnsaved) {
      const categoryIndex = newItems.findIndex(
        (item) => item.categoryId === deletedItem.categoryId
      );
      if (categoryIndex !== -1) {
        newItems.splice(categoryIndex, 1);
      }
    }

    const categoryIdForMoving = category.id;
    const targetCategory = newItems.find(
      (item) => item.category.id === categoryIdForMoving
    );
    if (!targetCategory) return;

    targetCategory.sections.push(...deletedCategory.sections);
    setItems([
      ...newItems.filter(
        (item) => item.category.id !== deletedItem.category.id
      ),
    ]);

    try {
      await deleteCategory({
        organizationId,
        projectId: caseId,
        categoryId: deletedItem.category.id,
        data: {
          target: { id: targetCategory.category.id },
          updatedAt: updatedAt,
        },
      });
      queryClient.invalidateQueries({
        queryKey: getGetDoclistQueryKey(organizationId, caseId),
      });
      queryClient.invalidateQueries({
        queryKey: getListCustomReportsByCaseQueryKey(organizationId, caseId),
      });
      toast({
        title: intl.formatMessage({
          id: "docList.deleteCategory.success",
          defaultMessage: "Category deleted",
        }),
        description: intl.formatMessage({
          id: "docList.deleteCategory.successDescription",
          defaultMessage: "The category has been successfully removed.",
        }),
      });
    } catch (error) {
      setItems([...items]);
      if (error instanceof AxiosError) {
        if (error.response?.status === 409) {
          setIsDoclistOutdated(true);
          return;
        }
      }
      toast({
        variant: "destructive",
        title: intl.formatMessage({
          id: "error",
          defaultMessage: "Error",
        }),
        description: intl.formatMessage({
          id: "docList.deleteCategory.error",
          defaultMessage: "Failed to delete the category. Please try again.",
        }),
      });
      logger.error("Failed to delete category:", error);
    }
  };

  const handleSortByNameClick = async (categoryId: number) => {
    if (!updatedAt) return;

    const currentOrder = sortByNameOrder[categoryId] === "asc" ? "desc" : "asc";
    setSortByNameOrder({ ...sortByNameOrder, [categoryId]: currentOrder });

    try {
      await sortSections({
        organizationId,
        projectId: caseId,
        categoryId,
        data: {
          sort: {
            order: currentOrder,
            type: "byName",
          },
          updatedAt: updatedAt,
        },
      });
      queryClient.invalidateQueries({
        queryKey: getGetDoclistQueryKey(organizationId, caseId),
      });
      queryClient.invalidateQueries({
        queryKey: getListCustomReportsByCaseQueryKey(organizationId, caseId),
      });
    } catch (error) {
      setItems([...items]);
      if (error instanceof AxiosError) {
        if (error.response?.status === 409) {
          setIsDoclistOutdated(true);
        }
      }
      logger.error("Failed to sort category sections by name:", error);
    }
  };

  const handleSortByDateClick = async (categoryId: number) => {
    if (!updatedAt) return;

    const currentOrder = sortByDateOrder[categoryId] === "asc" ? "desc" : "asc";
    setSortByDateOrder({ ...sortByDateOrder, [categoryId]: currentOrder });

    try {
      await sortSections({
        organizationId,
        projectId: caseId,
        categoryId,
        data: {
          sort: {
            order: currentOrder,
            type: "byDate",
          },
          updatedAt: updatedAt,
        },
      });
      queryClient.invalidateQueries({
        queryKey: getGetDoclistQueryKey(organizationId, caseId),
      });
      queryClient.invalidateQueries({
        queryKey: getListCustomReportsByCaseQueryKey(organizationId, caseId),
      });
    } catch (error) {
      setItems([...items]);
      if (error instanceof AxiosError) {
        if (error.response?.status === 409) {
          setIsDoclistOutdated(true);
        }
      }
      logger.error("Failed to sort category sections by date:", error);
    }
  };

  const handleSortByPageCountClick = async (categoryId: number) => {
    if (!updatedAt) return;

    const currentOrder =
      sortByPageCountOrder[categoryId] === "asc" ? "desc" : "asc";
    setSortByPageCountOrder({
      ...sortByPageCountOrder,
      [categoryId]: currentOrder,
    });

    try {
      await sortSections({
        organizationId,
        projectId: caseId,
        categoryId,
        data: {
          sort: {
            order: currentOrder,
            type: "byPageCount",
          },
          updatedAt: updatedAt,
        },
      });
      queryClient.invalidateQueries({
        queryKey: getGetDoclistQueryKey(organizationId, caseId),
      });
      queryClient.invalidateQueries({
        queryKey: getListCustomReportsByCaseQueryKey(organizationId, caseId),
      });
    } catch (error) {
      setItems([...items]);
      if (error instanceof AxiosError) {
        if (error.response?.status === 409) {
          setIsDoclistOutdated(true);
        }
      }
      logger.error("Failed to sort category sections by page count:", error);
    }
  };

  const handleMergeAllCategory = async (
    category: DoclistTreeItem,
    mergedSectionName: string
  ) => {
    if (!updatedAt || category.sections.length <= 1) return;

    trackEvent({
      eventName: "app/doc_list/merge_all_category/merge.button/click",
    });

    const targetSection = category.sections[0];

    const newItems = items.map((item) => {
      if (item.category.id === category.category.id) {
        const mergedSection = {
          ...targetSection,
          name: mergedSectionName,
          pageIds: category.sections.flatMap((s) => s.pageIds),
          pages: category.sections.flatMap((s) => s.pages ?? []),
          pageCount: category.sections.reduce((sum, s) => sum + s.pageCount, 0),
        };
        return {
          ...item,
          sections: [mergedSection],
        };
      }
      return item;
    });

    setItems(newItems);

    try {
      let currentUpdatedAt = updatedAt;

      const response = await mergeAllCategorySections({
        organizationId,
        projectId: caseId,
        chatId,
        data: {
          source: { id: category.category.id },
          target: { title: mergedSectionName },
          updatedAt: updatedAt,
        },
      });
      currentUpdatedAt = response.updatedAt;

      setUpdatedAt(caseId, currentUpdatedAt);
      queryClient.invalidateQueries({
        queryKey: getGetDoclistQueryKey(organizationId, caseId),
      });
      queryClient.invalidateQueries({
        queryKey: getListCustomReportsByCaseQueryKey(organizationId, caseId),
      });
    } catch (error) {
      logger.error("Failed to merge all sections:", error);
      setItems(items);
      if (error instanceof AxiosError) {
        if (error.response?.status === 409) {
          setIsDoclistOutdated(true);
        }
      }
      throw error;
    }
  };

  const handleMergeAllClick = (category: DoclistTreeItem) => {
    trackEvent({
      eventName: "app/doc_list/category_actions_menu/merge_all.link/click",
    });
    setMergeAllDialogData({ category });
  };

  const handleDoclistUpdate = () => {
    setIsDoclistOutdated(false);
    queryClient.invalidateQueries({
      queryKey: getGetDoclistQueryKey(organizationId, caseId),
    });
  };

  const handlePossibleDuplicatesClick = () => {
    setShowPossibleDuplicatesModal(true);
  };

  const handleRequestMoveSection = useCallback(
    (section: DoclistTreeItemSectionsItem) => {
      setSelectedSection(section);
      setShowMoveToAlertDialog(true);
    },
    []
  );

  const handleRequestSplitSection = useCallback(
    (section: DoclistTreeItemSectionsItem) => {
      setSelectedSection(section);
      setShowSplitSectionDialog(true);
    },
    []
  );

  const handleRequestMergeSection = useCallback(
    (section: DoclistTreeItemSectionsItem) => {
      setMergeDialogData({
        sourceSection: section,
        targetSection: null,
      });
      setShowMergeSectionDialog(true);
    },
    []
  );

  const handleRequestDeleteSection = useCallback(
    (section: DoclistTreeItemSectionsItem) => {
      setSelectedSection(section);
      setShowDeleteSectionDialog(true);
    },
    []
  );

  const handleRequestRotateSection = useCallback(
    (section: DoclistTreeItemSectionsItem) => {
      setSelectedSection(section);
      setShowRotateSectionDialog(true);
    },
    []
  );

  const handleSectionRotate = async (degrees: number) => {
    if (!selectedSection || !updatedAt) return;

    setIsRotatingSectionPages(true);

    const pages = selectedSection.pages || [];
    const pageIds = pages.map((p) => p.id);
    const newRotations = pages.map((p) => (p.rotate ?? 0) + degrees);
    const pageRotateMap = new Map<string, number>();

    pages.forEach((p, index) => {
      const newRotate = newRotations[index];
      pageRotateMap.set(p.id, newRotate);

      queryClient.setQueryData(
        getGetDoclistQueryKey(organizationId, caseId),
        updateDocListSectionPage(selectedSection.id, p.id, {
          rotate: newRotate,
        })
      );

      queryClient.setQueryData(["pageUrlsRotate", p.id], (oldData: any) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          rotate: newRotate,
        };
      });
    });

    try {
      const response = await rotateSectionPages({
        organizationId,
        projectId: caseId,
        chatId,
        data: {
          pageIds,
          degrees: newRotations.map((degree) => degree % 360),
          updatedAt,
        },
      });
      setUpdatedAt(caseId, response.updatedAt);
      pageRotateMap.forEach((newRotate, pageId) => {
        queryClient.setQueryData(["pageUrlsRotate", pageId], (oldData: any) => {
          if (!oldData) return oldData;
          if (oldData.rotate !== newRotate) {
            return {
              ...oldData,
              rotate: newRotate,
            };
          }
          return oldData;
        });
      });
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 409) {
          setIsDoclistOutdated(true);
          return;
        }
      }
      queryClient.invalidateQueries({
        queryKey: getGetDoclistQueryKey(organizationId, caseId),
      });
      toast({
        variant: "destructive",
        title: intl.formatMessage({
          id: "error",
          defaultMessage: "Error",
        }),
        description: intl.formatMessage({
          id: "docList.rotateSectionDialog.error",
          defaultMessage: "Failed to rotate pages. Please try again.",
        }),
      });
      logger.error("Failed to rotate section pages:", error);
      throw error;
    } finally {
      setIsRotatingSectionPages(false);
    }
  };

  const handleAutoRename = useCallback(
    async (section: DoclistTreeItemSectionsItem) => {
      if (!updatedAt) return;

      setAiRenamingSectionId(section.id);

      try {
        trackEvent({
          eventName: "app/doc_list/auto_rename_section.button/click",
          properties: { sectionId: section.id },
        });

        toast({
          title: intl.formatMessage({
            id: "docList.autoRename.started",
            defaultMessage: "Generating new title...",
          }),
        });

        const response = await regenerateSectionTitle({
          organizationId,
          projectId: caseId,
          chatId,
          sectionId: section.id,
          data: { updatedAt },
        });

        setUpdatedAt(caseId, response.updatedAt);
        queryClient.invalidateQueries({
          queryKey: getGetDoclistQueryKey(organizationId, caseId),
        });

        toast({
          title: intl.formatMessage({
            id: "docList.autoRename.success",
            defaultMessage: "Section renamed",
          }),
          description: intl.formatMessage(
            {
              id: "docList.autoRename.successDescription",
              defaultMessage: 'New title: "{title}"',
            },
            { title: response.title }
          ),
        });
      } catch (error) {
        if (error instanceof AxiosError) {
          if (error.response?.status === 409) {
            setIsDoclistOutdated(true);
            return;
          }
        }
        toast({
          variant: "destructive",
          title: intl.formatMessage({
            id: "error",
            defaultMessage: "Error",
          }),
          description: intl.formatMessage({
            id: "docList.autoRename.error",
            defaultMessage:
              "Failed to regenerate section title. Please try again.",
          }),
        });
        logger.error("Failed to auto-rename section:", error);
      } finally {
        setAiRenamingSectionId(null);
      }
    },
    [updatedAt, organizationId, caseId, queryClient, toast, intl]
  );

  const handleMergeSelectedSections = useCallback(async () => {
    if (selectedSectionsForMerge.size < 2 || !updatedAt) return;

    const allSections = items.flatMap((item) => item.sections);
    const sectionsInOrder = allSections.filter((s) =>
      selectedSectionsForMerge.has(s.id)
    );

    if (sectionsInOrder.length < 2) return;

    setIsMergingSelectedSections(true);

    try {
      let targetSection = sectionsInOrder[0];
      const response = await mergeSectionsMultiple({
        organizationId,
        projectId: caseId,
        chatId,
        data: {
          source: { sectionIds: sectionsInOrder.slice(1).map((s) => s.id) },
          target: { id: targetSection.id, title: targetSection.name },
          updatedAt: updatedAt,
        },
      });
      setUpdatedAt(caseId, response.updatedAt);
      queryClient.invalidateQueries({
        queryKey: getGetDoclistQueryKey(organizationId, caseId),
      });
      queryClient.invalidateQueries({
        queryKey: getListCustomReportsByCaseQueryKey(organizationId, caseId),
      });

      setSelectedSectionsForMerge(new Set());
      setLastSelectedSectionId(null);
    } catch (error) {
      logger.error("Failed to merge selected sections:", error);
      queryClient.invalidateQueries({
        queryKey: getGetDoclistQueryKey(organizationId, caseId),
      });
      queryClient.invalidateQueries({
        queryKey: getListCustomReportsByCaseQueryKey(organizationId, caseId),
      });

      if (error instanceof AxiosError && error.response?.status === 409) {
        // Keep the selection so the merge can be retried after refreshing.
        setIsDoclistOutdated(true);
        return;
      }

      toast({
        title: intl.formatMessage({ id: "docList.merge.failure" }),
        description: intl.formatMessage({
          id: "docList.merge.failureDescription",
        }),
        variant: "destructive",
      });
    } finally {
      setIsMergingSelectedSections(false);
    }
  }, [
    selectedSectionsForMerge,
    items,
    updatedAt,
    queryClient,
    organizationId,
    caseId,
    toast,
    intl,
  ]);

  const handleBulkAutoRename = useCallback(async () => {
    if (selectedSectionsForMerge.size === 0 || !updatedAt) return;

    const allSectionsFlat = items.flatMap((item) => item.sections);
    const sectionsToRename = allSectionsFlat.filter((s) =>
      selectedSectionsForMerge.has(s.id)
    );

    if (sectionsToRename.length === 0) return;

    setIsAiRenamingMultiple(true);

    trackEvent({
      eventName: "app/doc_list/bulk_auto_rename_sections.button/click",
      properties: { sectionCount: sectionsToRename.length },
    });

    toast({
      title: intl.formatMessage(
        {
          id: "docList.bulkAutoRename.started",
          defaultMessage: "Renaming {count} sections...",
        },
        { count: sectionsToRename.length }
      ),
    });

    try {
      const sectionIds = sectionsToRename.map((s) => s.id);
      const response = await regenerateSectionTitlesMultiple(
        organizationId,
        caseId,
        chatId,
        { sectionIds, updatedAt }
      );

      const successCount = response.results.filter(
        (r) => r.status === "success"
      ).length;
      const failCount = response.results.filter(
        (r) => r.status === "error"
      ).length;

      setUpdatedAt(caseId, response.updatedAt);
      queryClient.invalidateQueries({
        queryKey: getGetDoclistQueryKey(organizationId, caseId),
      });

      if (failCount === 0) {
        toast({
          title: intl.formatMessage(
            {
              id: "docList.bulkAutoRename.success",
              defaultMessage:
                "{count, plural, one {# section} other {# sections}} renamed",
            },
            { count: successCount }
          ),
        });
      } else {
        toast({
          variant: "destructive",
          title: intl.formatMessage({
            id: "docList.bulkAutoRename.partialSuccess",
            defaultMessage: "Some sections could not be renamed",
          }),
          description: intl.formatMessage(
            {
              id: "docList.bulkAutoRename.partialSuccessDescription",
              defaultMessage: "{success} renamed, {fail} failed",
            },
            { success: successCount, fail: failCount }
          ),
        });
      }
    } catch (error) {
      if (error instanceof AxiosError && error.response?.status === 409) {
        setIsDoclistOutdated(true);
      } else {
        logger.error("Failed to bulk auto-rename sections:", error);
        toast({
          variant: "destructive",
          title: intl.formatMessage({
            id: "docList.bulkAutoRename.failure",
            defaultMessage: "Failed to rename sections",
          }),
        });
      }
      queryClient.invalidateQueries({
        queryKey: getGetDoclistQueryKey(organizationId, caseId),
      });
    } finally {
      setSelectedSectionsForMerge(new Set());
      setLastSelectedSectionId(null);
      setIsAiRenamingMultiple(false);
    }
  }, [
    selectedSectionsForMerge,
    items,
    updatedAt,
    organizationId,
    caseId,
    chatId,
    queryClient,
    toast,
    intl,
  ]);

  const allSections = useMemo(
    () => items.flatMap((item) => item.sections),
    [items]
  );
  const hasDuplicateSections = useMemo(
    () => hasAnyDuplicates(allSections),
    [allSections]
  );

  const orderedPageIds = useMemo(() => {
    return items.flatMap((item) =>
      item.sections.flatMap((section) => section.pageIds ?? [])
    );
  }, [items]);

  const handleMultiSelect = useCallback(
    (pageId: string, modifier: "none" | "shift" | "cmd") => {
      if (isRestricted) return;
      handleMultiSelectPageClick(pageId, modifier, orderedPageIds);
    },
    [handleMultiSelectPageClick, orderedPageIds, isRestricted]
  );

  const allCategories = useMemo(() => {
    const unsavedCategories = categoryPlaceholders.map((placeholder) => ({
      id: placeholder.id,
      name: placeholder.name,
      isUnsaved: true,
      isFallback: false,
      color: placeholder.color,
    }));
    const savedCategories = items.map((item) => ({
      id: item.category.id,
      name: item.category.name,
      isUnsaved: false,
      isFallback: item.category.isFallback,
      color: item.category.color,
    }));
    return [...unsavedCategories, ...savedCategories];
  }, [categoryPlaceholders, items]);

  //dnd-kit for pages
  const {
    activeId,
    selectedPageId,
    setSelectedPageId,
    sensors,
    handleDragPageStart,
    handleDragPageEnd,
    openSections,
    setOpenSections,
  } = useDndForPages(
    items,
    setItems,
    organizationId,
    caseId,
    chatId,
    isRestricted,
    () => {
      if (!isMergingSelectedSections) {
        setIsDoclistOutdated(true);
      }
    }
  );

  const lastScrolledParamsRef = useRef<{
    pageId: string | null;
    sectionId: string | null;
  }>({ pageId: null, sectionId: null });

  const skipSectionAutoExpandRef = useRef(false);
  const lastActiveSectionIdRef = useRef<string | null>(null);
  const manuallyExpandedSectionsRef = useRef<Set<string>>(new Set());

  // Prune stale section IDs (deleted/merged) from the manual pin set.
  useEffect(() => {
    const manualSet = manuallyExpandedSectionsRef.current;
    if (manualSet.size === 0 || items.length === 0) return;
    const validIds = new Set(
      items.flatMap((item) => item.sections.map((s) => s.id))
    );
    let pruned = false;
    for (const id of manualSet) {
      if (!validIds.has(id)) {
        manualSet.delete(id);
        pruned = true;
      }
    }
    if (pruned) {
      setOpenSections((prev) => prev.filter((id) => validIds.has(id)));
    }
  }, [items, setOpenSections]);

  useEffect(() => {
    const shouldSkipAutoExpand = skipSectionAutoExpandRef.current;
    skipSectionAutoExpandRef.current = false;

    if (items.length === 0) return;

    const paramsChanged =
      lastScrolledParamsRef.current.pageId !== pageId ||
      lastScrolledParamsRef.current.sectionId !== sectionId;
    if (!paramsChanged) return;

    let targetSectionId: string | null = null;
    let targetCategoryId: number | null = null;

    if (pageId) {
      for (const item of items) {
        const matchedSection = item.sections.find((s) =>
          s.pageIds?.includes(pageId)
        );
        if (matchedSection) {
          targetSectionId = matchedSection.id;
          targetCategoryId = item.category.id;
          break;
        }
      }
    } else if (sectionId) {
      for (const item of items) {
        if (item.sections.some((s) => s.id === sectionId)) {
          targetSectionId = sectionId;
          targetCategoryId = item.category.id;
          break;
        }
      }
    }

    if (!targetSectionId || targetCategoryId === null) return;

    lastScrolledParamsRef.current = { pageId, sectionId };

    // Whether the active document (section) changed, i.e. the user switched to
    // a different document or category rather than scrolling within the same
    // one. Previously expanded sections only collapse on such a switch.
    const sectionChanged = lastActiveSectionIdRef.current !== targetSectionId;
    lastActiveSectionIdRef.current = targetSectionId;

    const categoryKey = `item-${targetCategoryId}`;

    setExpandedItems((prev) => {
      if (prev.includes(categoryKey)) return prev;
      return [...prev, categoryKey];
    });

    if (shouldSkipAutoExpand) {
      // Selecting a section/category navigates without auto-expanding it.
      // Collapse auto-expanded sections; keep manually expanded ones.
      if (sectionChanged) {
        setOpenSections((prev) => {
          const manualSet = manuallyExpandedSectionsRef.current;
          return prev.filter(
            (id) => manualSet.has(id) || id === targetSectionId
          );
        });
      }
      return;
    }

    setOpenSections((prev) => {
      const manualSet = manuallyExpandedSectionsRef.current;
      const manualSections = prev.filter((id) => manualSet.has(id));

      if (sectionChanged) {
        // Target is already manually pinned — no auto section needed.
        if (manualSet.has(targetSectionId)) return manualSections;
        // Replace auto section(s) with the new target.
        return [...manualSections, targetSectionId];
      }

      if (prev.includes(targetSectionId)) return prev;
      // Re-add the target as the sole auto-expanded section.
      return [...manualSections, targetSectionId];
    });

    const scrollSectionId = targetSectionId;
    const ACCORDION_ANIMATION_MS = 300;
    const timerId = setTimeout(() => {
      const sectionElement = document.querySelector(
        `[data-section-id="${scrollSectionId}"]`
      );
      sectionElement?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, ACCORDION_ANIMATION_MS);

    return () => clearTimeout(timerId);
  }, [pageId, sectionId, items, setOpenSections]);

  const handlePageSelect = (pageId: string) => {
    setSelectedPageId((prev) => (prev === pageId ? null : pageId));
  };

  const handleToggleSectionExpand = useCallback(
    (sectionId: string) => {
      const manualSet = manuallyExpandedSectionsRef.current;

      setOpenSections((prev) => {
        if (prev.includes(sectionId)) {
          manualSet.delete(sectionId);
          return prev.filter((id) => id !== sectionId);
        }

        manualSet.add(sectionId);

        // Evict the oldest manually expanded section when over the cap.
        if (manualSet.size > MAX_MANUALLY_EXPANDED_SECTIONS) {
          const [oldest] = manualSet;
          if (oldest) {
            manualSet.delete(oldest);
            return [...prev.filter((id) => id !== oldest), sectionId];
          }
        }

        return [...prev, sectionId];
      });
    },
    [setOpenSections]
  );

  const handleToggleCategoryExpand = useCallback((categoryKey: string) => {
    setExpandedItems((prev) =>
      prev.includes(categoryKey)
        ? prev.filter((id) => id !== categoryKey)
        : [...prev, categoryKey]
    );
  }, []);

  const findPage = (id: string): PageInfo | null => {
    for (const item of items) {
      for (const section of item.sections) {
        const pageIndex = section.pageIds?.findIndex((p) => p === id);
        const page = section.pages?.find((p) => p.id === id);
        if (pageIndex !== -1 && pageIndex !== undefined) {
          return {
            id: id,
            pageNumber: pageIndex + 1,
            sectionId: section.id,
            pageImg: page?.previewUrl ?? null,
            index: pageIndex,
            rotate: page?.rotate ?? 0,
          };
        }
      }
    }
    return null;
  };

  const handleSectionsDelete = async (sections: Set<string>) => {
    if (!updatedAt) return;
    const response = await deleteSections({
      organizationId,
      projectId: caseId,
      chatId,
      data: {
        sectionIds: [...sections.values()],
        updatedAt: updatedAt,
      },
    });
    setUpdatedAt(caseId, response.updatedAt);
    queryClient.invalidateQueries({
      queryKey: getGetDoclistQueryKey(organizationId, caseId),
    });
    queryClient.invalidateQueries({
      queryKey: getListCustomReportsByCaseQueryKey(organizationId, caseId),
    });
  };

  const onSectionDelete = async () => {
    if (!updatedAt) return;
    if (selectedSectionsForMerge.size > 0) {
      try {
        await handleSectionsDelete(selectedSectionsForMerge);
        setSelectedSectionsForMerge(new Set());
        setLastSelectedSectionId(null);
        toast({
          title: intl.formatMessage({
            id: "docList.deleteSections.success",
            defaultMessage: "Sections deleted",
          }),
          description: intl.formatMessage({
            id: "docList.deleteSections.successDescription",
            defaultMessage: "The sections have been successfully removed.",
          }),
        });
      } catch (error) {
        if (error instanceof AxiosError) {
          if (error.response?.status === 409) {
            setIsDoclistOutdated(true);
            return;
          }
        }
        toast({
          variant: "destructive",
          title: intl.formatMessage({
            id: "error",
            defaultMessage: "Error",
          }),
          description: intl.formatMessage({
            id: "docList.deleteSections.error",
            defaultMessage: "Failed to delete the sections. Please try again.",
          }),
        });
        logger.error("Failed to delete sections:", error);
      } finally {
        setIsMergingSelectedSections(false);
      }
    } else if (selectedSection) {
      await handleSectionDelete(selectedSection);
    }

    setShowDeleteSectionDialog(false);
    setSelectedSection(null);
  };

  const onSectionsMoveTo = async (category: CategoryItem) => {
    if (!updatedAt) return;

    if (selectedSectionsForMerge.size > 0) {
      try {
        if (category.isUnsaved) {
          const response = await createCategoryMultiple({
            organizationId,
            projectId: caseId,
            data: {
              source: {
                sectionIds: [...selectedSectionsForMerge.values()],
                title: category.name,
                color: category.color,
              },
              updatedAt,
            },
          });
          setUpdatedAt(caseId, response.updatedAt);
          queryClient.invalidateQueries({
            queryKey: getGetDoclistQueryKey(organizationId, caseId),
          });
          queryClient.invalidateQueries({
            queryKey: getListCustomReportsByCaseQueryKey(
              organizationId,
              caseId
            ),
          });
          setCategoryPlaceholders((prev) =>
            prev.filter((p) => p.id !== category.id)
          );
        } else {
          const destCategoryIndex = items.findIndex(
            (item) => item.category.id === category.id
          );

          if (destCategoryIndex === -1) return;
          const destCategory = items[destCategoryIndex];
          const prevId =
            destCategory?.sections[destCategory.sections.length - 1].id;

          const response = await moveSections({
            organizationId,
            projectId: caseId,
            data: {
              sectionIds: [...selectedSectionsForMerge.values()],
              target: {
                prevId: prevId,
                folderCategoryId: category.id,
              },
              updatedAt: updatedAt,
            },
          });
          setUpdatedAt(caseId, response.updatedAt);
          queryClient.invalidateQueries({
            queryKey: getGetDoclistQueryKey(organizationId, caseId),
          });
          queryClient.invalidateQueries({
            queryKey: getListCustomReportsByCaseQueryKey(
              organizationId,
              caseId
            ),
          });
        }
        setSelectedSectionsForMerge(new Set());
        setLastSelectedSectionId(null);
        toast({
          title: intl.formatMessage({
            id: "docList.moveSections.success",
            defaultMessage: "Sections moved",
          }),
          description: intl.formatMessage({
            id: "docList.moveSections.successDescription",
            defaultMessage: "The sections have been successfully moved.",
          }),
        });
      } catch (error) {
        setItems([...items]);
        if (error instanceof AxiosError) {
          if (error.response?.status === 409) {
            setIsDoclistOutdated(true);
            return;
          }
        }
        toast({
          variant: "destructive",
          title: intl.formatMessage({
            id: "error",
            defaultMessage: "Error",
          }),
          description: intl.formatMessage({
            id: "docList.moveSections.error",
            defaultMessage: "Failed to move the sections. Please try again.",
          }),
        });
        logger.error("Failed to move sections:", error);
      } finally {
        setIsMergingSelectedSections(false);
      }
    } else if (selectedSection) {
      await handleMoveSection(selectedSection, category);
    }

    setShowMoveToAlertDialog(false);
    setSelectedSection(null);
  };

  const handlePagesRotate = async () => {
    if (!updatedAt) return;

    setIsRotatingPages(true);

    const validPages = multiSelectedPageIds
      .map((pageId) => ({ pageId, pageInfo: findPage(pageId) }))
      .filter(
        (
          p
        ): p is { pageId: string; pageInfo: NonNullable<typeof p.pageInfo> } =>
          p.pageInfo !== undefined
      );

    if (validPages.length === 0) {
      setIsRotatingPages(false);
      return;
    }

    const pageIds = validPages.map((p) => p.pageId);
    const rotateMap = validPages.map((p) => {
      const currentRotate = p.pageInfo.rotate ?? 0;
      return currentRotate + 90;
    });
    const pageRotateMap = new Map<string, number>();

    validPages.forEach((p, index) => {
      const { sectionId } = p.pageInfo;
      const newRotate = rotateMap[index];
      pageRotateMap.set(p.pageId, newRotate);

      queryClient.setQueryData(
        getGetDoclistQueryKey(organizationId, caseId),
        updateDocListSectionPage(sectionId, p.pageId, { rotate: newRotate })
      );

      queryClient.setQueryData(["pageUrlsRotate", p.pageId], (oldData: any) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          rotate: newRotate,
        };
      });
    });

    try {
      const response = await rotateSectionPages({
        organizationId,
        projectId: caseId,
        chatId,
        data: {
          pageIds,
          degrees: rotateMap.map((degree) => degree % 360),
          updatedAt,
        },
      });
      setUpdatedAt(caseId, response.updatedAt);
      pageRotateMap.forEach((newRotate, pageId) => {
        queryClient.setQueryData(["pageUrlsRotate", pageId], (oldData: any) => {
          if (!oldData) return oldData;
          if (oldData.rotate !== newRotate) {
            return {
              ...oldData,
              rotate: newRotate,
            };
          }
          return oldData;
        });
      });
    } catch (error) {
      queryClient.invalidateQueries({
        queryKey: getGetDoclistQueryKey(organizationId, caseId),
      });
      if (error instanceof AxiosError) {
        if (error.response?.status === 409) {
          setIsDoclistOutdated(true);
          return;
        }
      }
      logger.error("Failed to rotate pages:", error);
    } finally {
      setIsRotatingPages(false);
    }
  };

  const handlePagesDelete = async () => {
    if (!updatedAt) return;
    setIsDeletingPages(true);
    try {
      const response = await deletePage({
        organizationId,
        projectId: caseId,
        chatId,
        data: {
          pageIds: multiSelectedPageIds,
          updatedAt: updatedAt,
        },
      });
      setUpdatedAt(caseId, response.updatedAt);
      queryClient.invalidateQueries({
        queryKey: getGetDoclistQueryKey(organizationId, caseId),
      });
      queryClient.invalidateQueries({
        queryKey: getListCustomReportsByCaseQueryKey(organizationId, caseId),
      });
      toast({
        title: intl.formatMessage({
          id: "docViewer.deletePage.success",
          defaultMessage: "Page deleted",
        }),
        description: intl.formatMessage({
          id: "docViewer.deletePage.successDescription",
          defaultMessage: "The page has been successfully removed.",
        }),
      });
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 409) {
          setIsDoclistOutdated(true);
          return;
        }
      }
      toast({
        variant: "destructive",
        title: intl.formatMessage({
          id: "error",
          defaultMessage: "Error",
        }),
        description: intl.formatMessage({
          id: "docViewer.deletePage.error",
          defaultMessage: "Failed to delete the page. Please try again.",
        }),
      });
    } finally {
      clearMultiSelection();
      setShowBulkDeleteDialog(false);
      setIsDeletingPages(false);
    }
  };

  const handlePagesExtract = async (
    category: CategoryItem,
    documentName: string
  ) => {
    if (!updatedAt) return;
    setIsExtractingPages(true);
    try {
      let response;
      if (category.isUnsaved) {
        response = await extractPagesToNewCategory({
          organizationId,
          projectId: caseId,
          chatId,
          data: {
            pageIds: multiSelectedPageIds,
            target: {
              categoryTitle: category.name,
              filename: documentName,
              categoryColor: category.color,
            },
            updatedAt: updatedAt,
          },
        });
        setCategoryPlaceholders((prev) =>
          prev.filter((p) => p.id !== category.id)
        );
      } else {
        response = await extractPages({
          organizationId,
          projectId: caseId,
          chatId,
          data: {
            pageIds: multiSelectedPageIds,
            target: {
              categoryId: category.id,
              filename: documentName,
            },
            updatedAt: updatedAt,
          },
        });
      }
      setUpdatedAt(caseId, response.updatedAt);
      queryClient.invalidateQueries({
        queryKey: getGetDoclistQueryKey(organizationId, caseId),
      });
      queryClient.invalidateQueries({
        queryKey: getListCustomReportsByCaseQueryKey(organizationId, caseId),
      });
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 409) {
          setIsDoclistOutdated(true);
          return;
        }
      }
      toast({
        variant: "destructive",
        title: intl.formatMessage({
          id: "error",
          defaultMessage: "Error",
        }),
        description: intl.formatMessage({
          id: "docList.extractPage.error",
          defaultMessage: "Failed to extract the pages. Please try again.",
        }),
      });
    } finally {
      clearMultiSelection();
      setShowExtractPagesDialog(false);
      setIsExtractingPages(false);
    }
  };

  const availableCategories = useMemo(() => {
    if (!selectedSection) return allCategories;
    return allCategories.filter((c) => c.id !== selectedSection.categoryId);
  }, [allCategories, selectedSection]);

  return (
    <>
      {defineCategoryTarget && (
        <DefineCategoryModal
          open={showDefineCategoryModal}
          onOpenChange={setShowDefineCategoryModal}
          organizationId={organizationId}
          projectId={caseId}
          folderCategoryId={defineCategoryTarget.category.id}
          folderCategoryName={defineCategoryTarget.category.name}
          sectionCount={defineCategoryTarget.sections.length}
          mode={defineCategoryMode}
        />
      )}
      {showDeleteCategoryDialog && deletedItem && (
        <DeleteCategoryDialog
          open={showDeleteCategoryDialog}
          setOpen={setShowDeleteCategoryDialog}
          item={deletedItem}
          categories={allCategories.filter(
            (c) => c.id !== deletedItem.category.id
          )}
          onCancel={handleDeleteCategoryCancel}
          onDelete={handleDeleteCategoryConfirm}
        />
      )}
      {showCancelExportDialog && (
        <CancelExportDialog
          open={showCancelExportDialog}
          setOpen={setShowCancelExportDialog}
          onKeep={handleKeepExporting}
          onDiscard={handleDiscardExporting}
          filesSelected={exportSectionIds.length}
        />
      )}
      {isDoclistOutdated && (
        <DoclistOutdatedDialog
          open={isDoclistOutdated}
          setOpen={setIsDoclistOutdated}
          onCancel={() => setIsDoclistOutdated(false)}
          onUpdate={handleDoclistUpdate}
        />
      )}
      {showMergeSectionDialog && mergeDialogData && (
        <MergeSectionDialog
          open={showMergeSectionDialog}
          setOpen={setShowMergeSectionDialog}
          section={mergeDialogData.sourceSection}
          items={items}
          onMerge={handleSectionMerge}
          defaultTargetSection={mergeDialogData.targetSection}
        />
      )}

      {selectedSection && (
        <DeletePageDialog
          open={showDeleteSectionDialog}
          setOpen={setShowDeleteSectionDialog}
          onDelete={onSectionDelete}
          onCancel={() => setShowDeleteSectionDialog(false)}
          type="section"
          name={selectedSection.name}
          selectedCount={
            selectedSectionsForMerge.size > 0
              ? selectedSectionsForMerge.size
              : 1
          }
          sectionNames={
            selectedSectionsForMerge.size > 0
              ? items
                  .flatMap((item) => item.sections)
                  .filter((s) => selectedSectionsForMerge.has(s.id))
                  .map((s) => s.name)
              : []
          }
        />
      )}

      {selectedSection && (
        <SplitSectionDialog
          open={showSplitSectionDialog}
          setOpen={setShowSplitSectionDialog}
          section={selectedSection}
          onSplit={handleSectionSplit}
        />
      )}

      {selectedSection && (
        <RotateSectionPagesDialog
          open={showRotateSectionDialog}
          setOpen={setShowRotateSectionDialog}
          section={selectedSection}
          onRotate={handleSectionRotate}
          isRotating={isRotatingSectionPages}
        />
      )}

      <MoveToAlertDialog
        open={showMoveToAlertDialog}
        setOpen={setShowMoveToAlertDialog}
        categories={availableCategories}
        onConfirm={(category) => {
          onSectionsMoveTo(category);
        }}
      />

      <PossibleDuplicatesModal
        open={showPossibleDuplicatesModal}
        setOpen={setShowPossibleDuplicatesModal}
        sections={allSections}
        onDeleteSections={handleSectionsDelete}
        onDoclistOutdated={() => setIsDoclistOutdated(true)}
      />
      <div
        ref={containerRef}
        data-tour="doc-list"
        className="h-full flex flex-col gap-3 pt-3 pl-3 relative"
      >
        <div className="justify-between items-center gap-3 inline-flex h-10 pr-3">
          <DocListActions
            mode={mode}
            setMode={setMode}
            isExportEmpty={exportSectionIds.length === 0}
            isExporting={isExporting || isExportingDocx}
            successfullyExport={isSuccessfullyExported}
            onExport={handleExport}
            onExportCancel={handleCancelExportClick}
            onAddCategory={handleAddCategory}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            isViewOnly={isRestricted}
            onPossibleDuplicatesClick={handlePossibleDuplicatesClick}
            hasDuplicateSections={hasDuplicateSections}
            hideDocxExport={selectedPageCount > EXPORT_WITH_DOCLIST_MAX_PAGES}
          />
        </div>
        <AnimatePresence>
          {selectedSectionsForMerge.size > 1 &&
            !isExportMode &&
            !isRestricted && (
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{
                  y: 0,
                  opacity: 1,
                  transition: { duration: 0.2, ease: "easeOut" },
                }}
                exit={{
                  y: 20,
                  opacity: 0,
                  transition: { duration: 0.15, ease: "easeIn" },
                }}
                className="absolute bottom-3 left-3 right-3 z-20 flex items-center justify-between bg-blue-600 rounded-lg shadow-[0_-4px_20px_rgba(59,130,246,0.3)] px-4 pr-2 py-2"
              >
                <span className="text-sm text-white font-normal">
                  <FormattedMessage
                    id="docList.selectedSections"
                    defaultMessage="{count} selected"
                    values={{ count: selectedSectionsForMerge.size }}
                  />
                </span>
                <div className="flex items-center gap-1">
                  {isWideContainer ? (
                    <Button
                      variant="outline"
                      className="bg-brand hover:bg-blue-700 border-blue-400 text-brand-foreground! h-8 px-3 text-sm"
                      onClick={() => {
                        const firstSelectedSection = items
                          .flatMap((item) => item.sections)
                          .find((s) => selectedSectionsForMerge.has(s.id));
                        if (firstSelectedSection) {
                          setSelectedSection(firstSelectedSection);
                          setShowMoveToAlertDialog(true);
                        }
                      }}
                    >
                      <ArrowDownToDot className="h-4 w-4 mr-1" />
                      <FormattedMessage
                        id="docList.move"
                        defaultMessage="Move"
                      />
                    </Button>
                  ) : (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="outline"
                          size="icon"
                          className="bg-brand hover:bg-blue-700 border-blue-400 text-brand-foreground! h-8 w-8"
                          onClick={() => {
                            const firstSelectedSection = items
                              .flatMap((item) => item.sections)
                              .find((s) => selectedSectionsForMerge.has(s.id));
                            if (firstSelectedSection) {
                              setSelectedSection(firstSelectedSection);
                              setShowMoveToAlertDialog(true);
                            }
                          }}
                        >
                          <ArrowDownToDot className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <FormattedMessage
                          id="docList.move"
                          defaultMessage="Move"
                        />
                      </TooltipContent>
                    </Tooltip>
                  )}
                  {isWideContainer ? (
                    <Button
                      variant="outline"
                      className="bg-brand hover:bg-blue-700 border-blue-400 text-brand-foreground! h-8 px-3 text-sm"
                      disabled={isAiRenamingMultiple}
                      onClick={handleBulkAutoRename}
                    >
                      <Sparkles className="h-4 w-4 mr-1" />
                      <FormattedMessage
                        id="docList.section.actionsDropdown.aiRename"
                        defaultMessage="AI Rename"
                      />
                    </Button>
                  ) : (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="outline"
                          size="icon"
                          className="bg-brand hover:bg-blue-700 border-blue-400 text-brand-foreground! h-8 w-8"
                          disabled={isAiRenamingMultiple}
                          onClick={handleBulkAutoRename}
                        >
                          <Sparkles className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <FormattedMessage
                          id="docList.section.actionsDropdown.aiRename"
                          defaultMessage="AI Rename"
                        />
                      </TooltipContent>
                    </Tooltip>
                  )}
                  {selectedSectionsForMerge.size >= 2 &&
                    (isWideContainer ? (
                      <Button
                        variant="outline"
                        className="bg-brand hover:bg-blue-700 border-blue-400 text-brand-foreground! h-8 px-3 text-sm"
                        onClick={handleMergeSelectedSections}
                      >
                        <Merge className="h-4 w-4 mr-1" />
                        <FormattedMessage
                          id="docList.merge"
                          defaultMessage="Merge"
                        />
                      </Button>
                    ) : (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="outline"
                            size="icon"
                            className="bg-brand hover:bg-blue-700 border-blue-400 text-brand-foreground! h-8 w-8"
                            onClick={handleMergeSelectedSections}
                          >
                            <Merge className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <FormattedMessage
                            id="docList.merge"
                            defaultMessage="Merge"
                          />
                        </TooltipContent>
                      </Tooltip>
                    ))}
                  {isWideContainer ? (
                    <Button
                      variant="outline"
                      className="bg-brand hover:bg-blue-700 border-blue-400 text-brand-foreground! h-8 px-3 text-sm"
                      onClick={() => {
                        const firstSelectedSection = items
                          .flatMap((item) => item.sections)
                          .find((s) => selectedSectionsForMerge.has(s.id));
                        if (firstSelectedSection) {
                          setSelectedSection(firstSelectedSection);
                          setShowDeleteSectionDialog(true);
                        }
                      }}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      <FormattedMessage id="delete" defaultMessage="Delete" />
                    </Button>
                  ) : (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="outline"
                          size="icon"
                          className="bg-brand hover:bg-blue-700 border-blue-400 text-brand-foreground! h-8 w-8"
                          onClick={() => {
                            const firstSelectedSection = items
                              .flatMap((item) => item.sections)
                              .find((s) => selectedSectionsForMerge.has(s.id));
                            if (firstSelectedSection) {
                              setSelectedSection(firstSelectedSection);
                              setShowDeleteSectionDialog(true);
                            }
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <FormattedMessage id="delete" defaultMessage="Delete" />
                      </TooltipContent>
                    </Tooltip>
                  )}
                  <Button
                    variant="outline"
                    size="icon"
                    className="bg-brand hover:bg-blue-700 border-transparent text-brand-foreground! h-8 w-8"
                    onClick={() => {
                      setSelectedSectionsForMerge(new Set());
                      setLastSelectedSectionId(null);
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </motion.div>
            )}
        </AnimatePresence>
        <AnimatePresence>
          {isMultiSelectActive() && !isExportMode && !isRestricted && (
            <FloatingActionBar
              selectedCount={multiSelectedPageIds.length}
              onExtract={() => setShowExtractPagesDialog(true)}
              onRotate={() => {
                trackEvent({
                  eventName: "app/doc_list/bulk_rotate.button/click",
                  properties: { pageCount: multiSelectedPageIds.length },
                });
                handlePagesRotate();
              }}
              isRotatePending={isRotatingPages}
              isExtractPending={isExtractingPages}
              isDeletePending={isDeletingPages}
              onDelete={() => setShowBulkDeleteDialog(true)}
              onClose={clearMultiSelection}
            />
          )}
        </AnimatePresence>
        <>
          {isDocListLoading ? (
            <div className="flex flex-col gap-2 h-full pr-3 w-full">
              <Skeleton className="h-[36px] w-full rounded-md animate-pulse" />
              <Skeleton className="h-[36px] w-full rounded-md animate-pulse" />
              <Skeleton className="h-[20px] w-[95%] rounded-md my-[6px] mx-[12px] animate-pulse" />
              <Skeleton className="h-[36px] w-full rounded-md animate-pulse" />
              <Skeleton className="h-[20px] w-[95%] rounded-md my-[6px] mx-[12px] animate-pulse" />
              <Skeleton className="h-[20px] w-[95%] rounded-md my-[6px] mx-[12px] animate-pulse" />
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto pr-1 scrollbar-gutter-stable">
              <DndContext
                sensors={sensors}
                onDragStart={handleDragPageStart}
                onDragEnd={handleDragPageEnd}
              >
                <DragDropContext
                  onDragEnd={handleDragEnd}
                  onDragStart={() => setIsDragging(true)}
                >
                  <Droppable
                    droppableId="categories"
                    type="category"
                    isDropDisabled={isExportMode || isRestricted}
                  >
                    {(provided) => (
                      <>
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          className="w-full flex flex-col gap-1"
                        >
                          {!isExportMode && isAddingCategory && (
                            <DocListCategoryInput
                              isNew
                              color={newCategoryColor!}
                              organizationId=""
                              projectId=""
                              onDoclistOutdated={() => {}}
                              onCancel={handleCategoryCancel}
                              onSubmit={handleCategoryCreated}
                            />
                          )}
                          {!isExportMode &&
                            categoryPlaceholders.map(({ id, color, name }) => (
                              <NewCategoryPlaceholder
                                key={id}
                                id={id}
                                name={name}
                                color={color}
                                onRename={handleCategoryPlaceholderRename}
                                onDelete={handleCategoryPlaceholderDelete}
                              />
                            ))}
                          <Accordion
                            type="multiple"
                            value={expandedItems}
                            onValueChange={setExpandedItems}
                            className="w-full flex flex-col gap-1"
                          >
                            {isExportMode && (
                              <div className="flex items-center px-3 py-2.5 gap-2">
                                <label className="flex items-center gap-2 cursor-pointer">
                                  <Checkbox
                                    checked={
                                      selectedCount ===
                                      items.flatMap((item) =>
                                        item.sections.map(
                                          (section) => section.id
                                        )
                                      ).length
                                    }
                                    onCheckedChange={handleSelectedCount}
                                    uncheckedIcon={
                                      selectedCount !== 0 ? (
                                        <Minus className="h-4 w-4 text-white" />
                                      ) : null
                                    }
                                    className={`mr-2 cursor-pointer data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600 border shadow-none ${selectedCount ? "bg-blue-600 border-blue-600" : "bg-background border-border"}`}
                                  />
                                  <span className="text-sm text-foreground">
                                    <FormattedMessage
                                      id="documentsList.selected"
                                      defaultMessage={"Selected: {count}"}
                                      values={{
                                        count: exportSectionIds.length,
                                      }}
                                    />
                                  </span>
                                </label>
                              </div>
                            )}
                            {deferredFilteredSections.map((item, index) => {
                              const categoryKey = `item-${item.category.id}`;
                              const isCategoryExpanded =
                                expandedItems.includes(categoryKey);

                              return (
                                <Draggable
                                  key={categoryKey}
                                  draggableId={categoryKey}
                                  index={index}
                                  isDragDisabled={
                                    isExportMode ||
                                    isRestricted ||
                                    isCategoryExpanded
                                  }
                                >
                                  {(provided) => (
                                    <AccordionItem
                                      value={categoryKey}
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      style={
                                        provided.draggableProps
                                          .style as React.CSSProperties
                                      }
                                    >
                                      {isCategoryRenaming &&
                                      item.category.id === renamedCategoryId ? (
                                        <DocListCategoryInput
                                          category={item}
                                          organizationId={organizationId}
                                          projectId={caseId}
                                          onDoclistOutdated={() =>
                                            setIsDoclistOutdated(true)
                                          }
                                          onCancel={() =>
                                            setIsCategoryRenaming(false)
                                          }
                                          setItems={setItems}
                                        />
                                      ) : (
                                        <AccordionTrigger
                                          expandOnlyByChevron={true}
                                          className={`text-sm rounded-md w-full group cursor-pointer! ${getCategoryTextColor(item.category.id)} ${isExportMode ? "pr-3" : "px-3 py-2"}`}
                                          style={{
                                            ...categoryBackgrounds.get(
                                              item.category.id
                                            ),
                                            opacity: isExporting ? "0.7" : "1",
                                          }}
                                          expandEventName={
                                            "app/doc_list/expand_category.button/click"
                                          }
                                          onMouseEnter={() =>
                                            setHoveredCategoryId(
                                              item.category.id
                                            )
                                          }
                                          onMouseLeave={() =>
                                            setHoveredCategoryId(null)
                                          }
                                          onDoubleClick={() => {
                                            if (
                                              !isExportMode &&
                                              !isRestricted
                                            ) {
                                              handleToggleCategoryExpand(
                                                categoryKey
                                              );
                                              trackEvent({
                                                eventName:
                                                  "app/doc_list/category_double_click_toggle.category/dblclick",
                                              });
                                            }
                                          }}
                                        >
                                          <div className="flex items-center w-full uppercase">
                                            {!isExportMode &&
                                              !isRestricted &&
                                              !isCategoryExpanded && (
                                                <div
                                                  {...provided.dragHandleProps}
                                                  className="hidden group-hover:block"
                                                >
                                                  <GripVertical className="h-4 w-4 min-w-4 text-white cursor-grab -ml-2 mr-2" />
                                                </div>
                                              )}
                                            {isExportMode && (
                                              <label
                                                className="flex items-center pl-3 pr-2 py-[10px] cursor-pointer"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                }}
                                              >
                                                <Checkbox
                                                  checked={
                                                    !!item.sections.length &&
                                                    item.sections.every(
                                                      (section) =>
                                                        selectedSectionIdsSet.has(
                                                          section.id
                                                        )
                                                    )
                                                  }
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                  }}
                                                  onCheckedChange={(
                                                    checked
                                                  ) => {
                                                    trackEvent({
                                                      eventName:
                                                        "app/doc_list/select_category.checkbox/click",
                                                      properties: {
                                                        categoryId:
                                                          item.category.id,
                                                        checked,
                                                      },
                                                    });
                                                    handleCategoryCheckboxChange(
                                                      item.category.id,
                                                      checked === true
                                                    );
                                                  }}
                                                  uncheckedIcon={
                                                    item.sections.some(
                                                      (section) =>
                                                        selectedSectionIdsSet.has(
                                                          section.id
                                                        )
                                                    ) ? (
                                                      <Minus className="h-4 w-4 text-white" />
                                                    ) : null
                                                  }
                                                  disabled={isExporting}
                                                  className={`cursor-pointer data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600 border shadow-none ${selectedCategoryIds.has(item.category.id) ? "bg-blue-600 border-blue-600" : "bg-background border-border"}`}
                                                />
                                              </label>
                                            )}
                                            <span className="truncate min-w-0">
                                              {item.category.name}
                                            </span>
                                            {!isExportMode && !isRestricted && (
                                              <CategoryActions
                                                className={clsx(
                                                  "transition-opacity duration-300",
                                                  editedCategoryId ===
                                                    item.category.id
                                                    ? "opacity-100"
                                                    : "opacity-0 group-hover:opacity-100"
                                                )}
                                                handleOpenChange={(isOpen) =>
                                                  setEditedCategoryId(
                                                    isOpen
                                                      ? item.category.id
                                                      : null
                                                  )
                                                }
                                                handleRename={() =>
                                                  handleRenameClick(item)
                                                }
                                                handleDelete={() =>
                                                  handleDeleteCategory(item)
                                                }
                                                handleDefineCategory={
                                                  hasOrgDefinition(item)
                                                    ? undefined
                                                    : () =>
                                                        handleDefineCategory(
                                                          item
                                                        )
                                                }
                                                isDefiningCategory={
                                                  showDefineCategoryModal &&
                                                  defineCategoryMode ===
                                                    "new" &&
                                                  defineCategoryTarget?.category
                                                    .id === item.category.id
                                                }
                                                handleRedefineCategory={
                                                  hasOrgDefinition(item)
                                                    ? () =>
                                                        handleRedefineCategory(
                                                          item
                                                        )
                                                    : undefined
                                                }
                                                isRedefiningCategory={
                                                  showDefineCategoryModal &&
                                                  defineCategoryMode ===
                                                    "redefine" &&
                                                  defineCategoryTarget?.category
                                                    .id === item.category.id
                                                }
                                                handleMergeAll={() =>
                                                  handleMergeAllClick(item)
                                                }
                                                categories={allCategories}
                                                handleSortByName={() => {
                                                  handleSortByNameClick(
                                                    item.category.id
                                                  );
                                                }}
                                                handleSortByDate={() => {
                                                  handleSortByDateClick(
                                                    item.category.id
                                                  );
                                                }}
                                                handleSortByPageCount={() => {
                                                  handleSortByPageCountClick(
                                                    item.category.id
                                                  );
                                                }}
                                                canBeSorted={
                                                  item.sections.length > 1
                                                }
                                              />
                                            )}
                                          </div>
                                        </AccordionTrigger>
                                      )}
                                      <AccordionContent className="p-[2px]">
                                        <Droppable
                                          droppableId={`category-${item.category.id}`}
                                          type="section"
                                          isDropDisabled={
                                            isExportMode || isRestricted
                                          }
                                        >
                                          {(provided) => (
                                            <Accordion
                                              type="multiple"
                                              value={openSections}
                                              onValueChange={(value) => {
                                                const manualSet =
                                                  manuallyExpandedSectionsRef.current;
                                                const added = value.filter(
                                                  (id) =>
                                                    !openSections.includes(id)
                                                );
                                                const removed =
                                                  openSections.filter(
                                                    (id) => !value.includes(id)
                                                  );

                                                for (const id of added)
                                                  manualSet.add(id);
                                                for (const id of removed)
                                                  manualSet.delete(id);

                                                let result = value;
                                                while (
                                                  manualSet.size >
                                                  MAX_MANUALLY_EXPANDED_SECTIONS
                                                ) {
                                                  const [oldest] = manualSet;
                                                  if (!oldest) break;
                                                  manualSet.delete(oldest);
                                                  result = result.filter(
                                                    (id) => id !== oldest
                                                  );
                                                }

                                                setOpenSections(result);
                                              }}
                                              ref={provided.innerRef}
                                              {...provided.droppableProps}
                                              className="pt-[2px] flex flex-col"
                                            >
                                              {item.sections.map(
                                                (section, sectionIndex) => (
                                                  <div key={section.id}>
                                                    {sectionIndex > 0 &&
                                                      !isRestricted && (
                                                        <div className="relative">
                                                          <MergeSectionButton
                                                            isDraggingOver={
                                                              !!activeId
                                                            }
                                                            isDragging={
                                                              isDragging
                                                            }
                                                            onHoverChange={(
                                                              isHovered
                                                            ) =>
                                                              setHoveredMergeLine(
                                                                isHovered
                                                                  ? {
                                                                      categoryId:
                                                                        item
                                                                          .category
                                                                          .id,
                                                                      index:
                                                                        sectionIndex,
                                                                    }
                                                                  : null
                                                              )
                                                            }
                                                            onMerge={() =>
                                                              handleMergeLineClick(
                                                                item.category
                                                                  .id,
                                                                sectionIndex
                                                              )
                                                            }
                                                          />
                                                        </div>
                                                      )}
                                                    <DocListCategorySection
                                                      key={section.id}
                                                      isActive={
                                                        section.id === sectionId
                                                      }
                                                      isCurrentSection={
                                                        !!pageId &&
                                                        section.pageIds?.includes(
                                                          pageId
                                                        )
                                                      }
                                                      organizationId={
                                                        organizationId
                                                      }
                                                      projectId={caseId}
                                                      chatId={chatId}
                                                      onDoclistOutdated={() =>
                                                        setIsDoclistOutdated(
                                                          true
                                                        )
                                                      }
                                                      section={section}
                                                      index={sectionIndex}
                                                      isExportMode={
                                                        isExportMode
                                                      }
                                                      isViewOnly={isRestricted}
                                                      isSelected={exportSectionIds.includes(
                                                        section.id
                                                      )}
                                                      query={searchTerm}
                                                      onCheckboxChange={
                                                        handleSectionCheckboxChange
                                                      }
                                                      onClick={
                                                        handleSectionClick
                                                      }
                                                      categories={allCategories}
                                                      onConfirmMoveTo={
                                                        handleRequestMoveSection
                                                      }
                                                      isProcessing={isExporting}
                                                      onSplit={
                                                        handleRequestSplitSection
                                                      }
                                                      items={items}
                                                      onMerge={
                                                        handleRequestMergeSection
                                                      }
                                                      selectedPageId={
                                                        selectedPageId
                                                      }
                                                      onPageSelect={
                                                        handlePageSelect
                                                      }
                                                      absolutePageNumbers={
                                                        absolutePageNumbers
                                                      }
                                                      multiSelectedPageIds={
                                                        multiSelectedPageIds
                                                      }
                                                      onMultiSelect={
                                                        handleMultiSelect
                                                      }
                                                      openSections={
                                                        openSections
                                                      }
                                                      className={clsx(
                                                        "transition-all duration-200",
                                                        !isDragging &&
                                                          hoveredMergeLine?.categoryId ===
                                                            item.category.id &&
                                                          (hoveredMergeLine.index ===
                                                            sectionIndex ||
                                                            hoveredMergeLine.index ===
                                                              sectionIndex +
                                                                1) &&
                                                          "bg-blue-50! dark:bg-blue-900/30! rounded-md! shadow-none!"
                                                      )}
                                                      onDeleteSection={
                                                        handleRequestDeleteSection
                                                      }
                                                      onRotate={
                                                        handleRequestRotateSection
                                                      }
                                                      onAutoRename={
                                                        handleAutoRename
                                                      }
                                                      isAiRenaming={
                                                        aiRenamingSectionId ===
                                                          section.id ||
                                                        (isAiRenamingMultiple &&
                                                          selectedSectionsForMerge.has(
                                                            section.id
                                                          ))
                                                      }
                                                      isSelectedForMerge={selectedSectionsForMerge.has(
                                                        section.id
                                                      )}
                                                      isMultiSelectMode={
                                                        selectedSectionsForMerge.size >
                                                        0
                                                      }
                                                      onToggleExpand={
                                                        handleToggleSectionExpand
                                                      }
                                                      pageAnnotationColors={
                                                        pageAnnotationColors
                                                      }
                                                    />
                                                  </div>
                                                )
                                              )}
                                              {provided.placeholder}
                                            </Accordion>
                                          )}
                                        </Droppable>
                                      </AccordionContent>
                                    </AccordionItem>
                                  )}
                                </Draggable>
                              );
                            })}
                          </Accordion>
                        </div>
                        <div className="h-[36px]">{provided.placeholder}</div>
                      </>
                    )}
                  </Droppable>
                </DragDropContext>
                <DragOverlay key={activeId || "empty"}>
                  {activeId
                    ? (() => {
                        const foundPage = findPage(activeId);
                        if (!foundPage) return null;

                        return (
                          <DocListPage
                            pageId={foundPage.id}
                            pageNumber={foundPage.pageNumber}
                            pageImg={foundPage.pageImg}
                            sectionId={foundPage.sectionId}
                            isDraggable={true}
                            index={foundPage.index}
                            isDragOverlay
                            isSelected={selectedPageId === foundPage.id}
                            onSelect={handlePageSelect}
                            absolutePageNumbers={absolutePageNumbers}
                          />
                        );
                      })()
                    : null}
                </DragOverlay>
              </DndContext>
            </div>
          )}
        </>
      </div>
      <ExportDocListDialog
        isOpen={isExportDialogOpen}
        onClose={() => {
          setIsExportDialogOpen(false);
          setNameOfExportedFile("");
        }}
        fileName={nameOfExportedFile}
      />
      <ExtractPagesDialog
        open={showExtractPagesDialog}
        onOpenChange={setShowExtractPagesDialog}
        selectedPageIds={multiSelectedPageIds}
        categories={allCategories}
        onExtract={(category: CategoryItem, documentName: string) => {
          handlePagesExtract(category, documentName);
        }}
      />
      <DeletePageDialog
        open={showBulkDeleteDialog}
        setOpen={setShowBulkDeleteDialog}
        selectedCount={multiSelectedPageIds.length}
        onCancel={() => setShowBulkDeleteDialog(false)}
        onDelete={() => {
          trackEvent({
            eventName: "app/doc_list/bulk_delete.button/click",
            properties: { pageCount: multiSelectedPageIds.length },
          });
          handlePagesDelete();
        }}
        isDeleting={isDeletingPages}
      />
      {mergeAllDialogData && (
        <MergeAllCategoryDialog
          open={!!mergeAllDialogData}
          setOpen={(open) => !open && setMergeAllDialogData(null)}
          category={mergeAllDialogData.category}
          isLoading={isAllSectionsMerging}
          onMergeAll={(mergedSectionName) =>
            handleMergeAllCategory(
              mergeAllDialogData.category,
              mergedSectionName
            )
          }
        />
      )}
      <Dialog open={isMergingSelectedSections}>
        <DialogContent
          className="sm:max-w-md"
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>
              <FormattedMessage
                id="docList.mergingSections"
                defaultMessage="Merging Sections"
              />
            </DialogTitle>
            <DialogDescription>
              <FormattedMessage
                id="docList.pleaseWait"
                defaultMessage="Please wait..."
              />
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </>
  );
};
